import { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import { useMsal } from "@azure/msal-react";
import { useUserProfile } from "../../auth/UserProfileContext";
import { supabase, dbToDeal, dealToDb, loadProperties, upsertProperty, updateProperty, loadContacts, upsertContact, deleteContact, loadOutreachLog, createOutreachEntry, updateOutreachEntry, loadInstitutionalOwners, loadAppSettings, updateAppSetting, deleteProperties } from "../../supabase";
import useMsaList from "../../hooks/useMsaList";
import { STAGES } from "../../constants/dealStages";
import { fmt } from "../../utils/formatters";
import { categorizeProperties } from "../../utils/categorization";
import { parseCoStarExcel } from "../../utils/costarParser";
import { generateLoiBody, generatePortfolioLoiBody, generateLoiHtml, generatePortfolioLoiHtml } from "../../utils/loiGenerator";
import OfferPricer from "./OfferPricer";
import { callClaude } from "../../services/claude";
import { geocodeAddress } from "../../services/geocoding";
import { sendMail } from "../../services/graphMail";
import { logActivity, autoIncrementTasks } from "../../services/activityLogger";
import { fetchInboxMessages, fetchConversationThread } from "../../services/graphInbox";
import iconWhite from "../../assets/logos/icon-white.png";

function buildPropertySourceNotes(property, outreachLog) {
  let notes = `Imported from Sourcing. Owner: ${property.owner_name || 'Unknown'}.`;
  const propOutreach = outreachLog.filter(o => o.property_id === property.id);
  if (propOutreach.length > 0) {
    const latest = propOutreach[0];
    notes += ` LOI sent by ${latest.sent_by} on ${new Date(latest.sent_at).toLocaleDateString()} at $${latest.loi_psf}/SF ($${latest.loi_total?.toLocaleString()} total).`;
    if (latest.status !== 'Sent') {
      notes += ` Status: ${latest.status}.`;
    }
  }
  if (property.ceiling_ht) notes += ` Clear height: ${property.ceiling_ht}.`;
  if (property.loading_docks) notes += ` ${property.loading_docks} loading docks.`;
  if (property.percent_leased != null) notes += ` ${property.percent_leased}% leased.`;
  if (property.year_built) notes += ` Built ${property.year_built}.`;
  return notes;
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE-SCOPED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
// These live outside DatabaseView so React sees a stable component identity
// across parent re-renders. Anything inside DatabaseView would get re-created
// on every state update, which forces React to unmount the subtree — and any
// inputs in that subtree lose focus mid-keystroke.

function BucketSectionStable({
  id, label, color, items, children,
  filterBucket, expandedBuckets, toggleBucket, handleDeleteBucket, searchTerm,
  ColumnHeader, PropertyRow,
}) {
  const filtered = filterBucket(items);
  return (
    <div className="mb-6">
      <div className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[rgba(44,44,44,0.5)] border-l-4 ${color} hover:bg-[#2c2c2c] transition-colors`}>
        <button
          onClick={() => toggleBucket(id)}
          className="flex items-center gap-3 flex-1 text-left"
        >
          <svg className={`w-4 h-4 text-[#8a8a8a] transition-transform ${expandedBuckets[id] ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-white font-semibold">{label}</span>
          <span className="bg-[#3a3a3a] text-[#b4b4b4] text-xs font-medium px-2 py-0.5 rounded-full">{filtered.length}</span>
          {/* Classification breakdown badges */}
          {(() => {
            const c1 = filtered.filter(p => p.classification === 1).length;
            const c2 = filtered.filter(p => p.classification === 2).length;
            const c3 = filtered.filter(p => p.classification === 3).length;
            if (!c1 && !c2 && !c3) return null;
            return (
              <span className="flex items-center gap-1 ml-1">
                {c1 > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(79,157,118,0.15)", color: "#4f9d76" }}>{c1}</span>}
                {c2 > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(200,146,47,0.15)", color: "#c8922f" }}>{c2}</span>}
                {c3 > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(233,53,41,0.15)", color: "#e93529" }}>{c3}</span>}
              </span>
            );
          })()}
        </button>
        {filtered.length > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteBucket(label, filtered); }}
            className="flex items-center gap-1.5 text-xs bg-[#3a3a3a] hover:bg-[#c42d22] text-[#b4b4b4] hover:text-white px-3 py-1.5 rounded-lg transition-colors"
            title={`Delete all ${filtered.length} propert${filtered.length === 1 ? 'y' : 'ies'} in ${label}${searchTerm ? ' (matching current search)' : ''}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
            Delete All
          </button>
        )}
      </div>
      {expandedBuckets[id] && filtered.length > 0 && (
        <div className="mt-3">
          <ColumnHeader />
          {typeof children === 'function' ? children(filtered) : filtered.map(p => <PropertyRow key={p.id} prop={p} bucket={id} />)}
        </div>
      )}
      {expandedBuckets[id] && filtered.length === 0 && (
        <p className="text-[#8a8a8a] text-sm px-4 py-3">No properties in this bucket{searchTerm ? ' matching your search' : ''}.</p>
      )}
    </div>
  );
}

function DatabaseView({ deals = [], onDealCreated, onNavigateToDeal }) {
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [outreachLog, setOutreachLog] = useState([]);
  const [institutionalOwners, setInstitutionalOwners] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [buckets, setBuckets] = useState({ ready: [], missing: [], recent: [], institutional: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBuckets, setExpandedBuckets] = useState({ ready: true, missing: true, recent: true, institutional: false });
  const [subTab, setSubTab] = useState('properties'); // 'properties' | 'contacts' | 'outreach'
  const [contactModal, setContactModal] = useState(null); // null | { mode: 'add'|'edit', contact?, prefill? }
  // emails is a dynamic array — UI-friendly. On save we mirror the first 3
  // entries to the email1/2/3 columns (DB schema) and stash any extras in
  // `email_extras` (a JSONB column on contacts that you'll add via SQL when
  // you're ready to truly support unlimited).
  const [contactFormData, setContactFormData] = useState({ name: '', title: '', owner_name: '', emails: [''], phone: '', contact_type: 'owner', property_id: '' });
  const [contactSearch, setContactSearch] = useState('');
  const [showTrashedEmails, setShowTrashedEmails] = useState(false);
  const [emailStatusFilter, setEmailStatusFilter] = useState(null);
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [contactUploading, setContactUploading] = useState(false);
  const [contactUploadStatus, setContactUploadStatus] = useState(null);
  const contactFileInputRef = useRef(null);
  const [toast, setToast] = useState(null);
  // LOI state — sender is always the signed-in user (full profile fetched from
  // Graph /me by UserProfileProvider), tracked in outreach_log.
  const { instance: msalInstance, accounts } = useMsal();
  const account = accounts[0];
  const { profile: userProfile, loading: profileLoading } = useUserProfile();
  const currentUser = {
    name: userProfile?.name || account?.name || account?.username || 'Unknown User',
    email: userProfile?.email || account?.username || '',
    phone: userProfile?.phone || '',
    jobTitle: userProfile?.jobTitle || '',
  };
  // loiModal carries the editable draft: subject, body, to, cc are user-overridable;
  // confirmStep flips to true after the user clicks Send so we show a one-screen
  // "are you sure?" summary before actually firing.
  const [loiModal, setLoiModal] = useState(null); // { prop, contact, psf, subject, body, to, cc, confirmStep, cooldownAck }
  const [sendingLoi, setSendingLoi] = useState(false);
  const [psfValues, setPsfValues] = useState({}); // { [propId]: string }
  // Outreach state
  const [outreachSearch, setOutreachSearch] = useState('');
  const [outreachStatusFilter, setOutreachStatusFilter] = useState('All');
  const [outreachSentByFilter, setOutreachSentByFilter] = useState('All');
  const [outreachSort, setOutreachSort] = useState('newest');
  const [outreachFollowUpOnly, setOutreachFollowUpOnly] = useState(false);
  // Email conversation thread modal for outreach entries
  const [conversationModal, setConversationModal] = useState(null); // { entry, thread: [], loading }
  const conversationEndRef = useRef(null);
  // Create Deal modal state
  const [createDealModal, setCreateDealModal] = useState(null);
  const [createDealStage, setCreateDealStage] = useState('Screening');
  const [createDealMarket, setCreateDealMarket] = useState('');
  const { msas: msaList } = useMsaList([createDealMarket]);
  const [createDealSaving, setCreateDealSaving] = useState(false);
  // Bulk selection + portfolio LOI state
  const [selectedProps, setSelectedProps] = useState(new Set());
  const [batchPsf, setBatchPsf] = useState('');
  const [ownerPsfValues, setOwnerPsfValues] = useState({}); // { ownerName: string }
  const [expandedOwners, setExpandedOwners] = useState({});
  const [portfolioLoiModal, setPortfolioLoiModal] = useState(null); // { groups: [{ owner, props, contact, psf }] }
  // Per-group overrides for the batch LOI modal. Keyed by `${gi}` group index.
  // Each entry: { subject?, body?, to?, cc? }. Missing keys fall back to the
  // generated defaults inside the send handlers.
  const [portfolioEdits, setPortfolioEdits] = useState({});
  // DB Chat state
  const [dbChatOpen, setDbChatOpen] = useState(false);
  const [dbChatMessages, setDbChatMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m Proxy, your sourcing assistant. Ask me about properties, contacts, outreach, or anything in sourcing.' },
  ]);
  const [dbChatInput, setDbChatInput] = useState('');
  const [dbChatLoading, setDbChatLoading] = useState(false);
  const dbChatBottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const DB_CHAT_CHIPS = [
    "Properties in South Florida",
    "Owners with 3+ properties",
    "LOIs sent this month",
    "Properties with 20'+ clear height",
    "Outreach response rate by market",
    "Ready properties over 50,000 SF",
  ];

  const buildDbSystemPrompt = () => {
    const readyCount = buckets.ready?.length || 0;
    const missingCount = buckets.missing?.length || 0;
    const recentCount = buckets.recent?.length || 0;
    const instCount = buckets.institutional?.length || 0;

    // Top owners
    const ownerCounts = {};
    properties.forEach(p => { if (p.owner_name) ownerCounts[p.owner_name] = (ownerCounts[p.owner_name] || 0) + 1; });
    const topOwners = Object.entries(ownerCounts).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([n, c]) => `  ${n}: ${c} properties`).join('\n');

    // Markets
    const marketCounts = {};
    properties.forEach(p => { if (p.market) marketCounts[p.market] = (marketCounts[p.market] || 0) + 1; });
    const markets = Object.entries(marketCounts).sort((a, b) => b[1] - a[1]).map(([m, c]) => `  ${m}: ${c}`).join('\n');

    // Outreach stats
    const sentEntries = outreachLog.filter(e => e.status === 'Sent');
    const respondedEntries = outreachLog.filter(e => e.status === 'Responded');
    const avgPsf = outreachLog.length > 0 ? (outreachLog.reduce((s, e) => s + (e.loi_psf || 0), 0) / outreachLog.length).toFixed(2) : '0';

    return `You are Proxy, the ProximityParks USA sourcing assistant. You help the acquisitions team analyze their off-market industrial property sourcing data.

SOURCING SUMMARY:
- Total properties: ${properties.length}
- Total contacts: ${contacts.length}
- Total LOIs sent: ${outreachLog.length}
- Properties by bucket: Ready (${readyCount}), Missing Contact (${missingCount}), Recently Contacted (${recentCount}), Institutional (${instCount})

MARKETS:
${markets}

TOP OWNERS BY PROPERTY COUNT:
${topOwners}

OUTREACH SUMMARY:
- Total LOIs sent: ${outreachLog.length}
- Awaiting response: ${sentEntries.length}
- Responded: ${respondedEntries.length}
- Average offer PSF: $${avgPsf}

APP SETTINGS:
- Cooldown: ${appSettings.cooldown_days || '14'} days
- Deposit: ${(parseFloat(appSettings.deposit_pct || '0.01') * 100).toFixed(1)}%
- Offer expiry: ${appSettings.offer_expiry_days || '16'} days

When the user asks to filter or find properties, respond with specific property details. When asked about outreach, reference actual LOI data. Be specific with numbers and addresses. Keep responses concise and actionable.`;
  };

  const sendDbChat = async (text) => {
    const msg = (text || dbChatInput).trim();
    if (!msg || dbChatLoading) return;
    setDbChatInput('');
    const userMsg = { role: 'user', content: msg };
    const newMessages = [...dbChatMessages, userMsg];
    setDbChatMessages(newMessages);
    setDbChatLoading(true);

    try {
      // Build context with relevant property data based on the query
      let contextData = '';
      const lowerMsg = msg.toLowerCase();

      // Include up to 50 matching property records for specific queries
      let relevantProps = properties;
      if (lowerMsg.includes('south florida') || lowerMsg.includes('miami') || lowerMsg.includes('doral') || lowerMsg.includes('fort lauderdale'))
        relevantProps = properties.filter(p => ['South Florida', 'Miami', 'Fort Lauderdale', 'Doral', 'Medley', 'Opa-locka', 'Pompano'].some(m => (p.market || '').includes(m) || (p.city || '').toLowerCase().includes(m.toLowerCase())));
      else if (lowerMsg.includes('dallas') || lowerMsg.includes('texas') || lowerMsg.includes('houston'))
        relevantProps = properties.filter(p => ['Dallas', 'Houston', 'San Antonio', 'Richardson/DFW'].some(m => (p.market || '').includes(m)));
      else if (lowerMsg.includes('boca') || lowerMsg.includes('delray') || lowerMsg.includes('palm beach'))
        relevantProps = properties.filter(p => (p.city || '').toLowerCase().includes('boca') || (p.city || '').toLowerCase().includes('delray') || (p.market || '').includes('Palm Beach'));
      else if (lowerMsg.includes('ready'))
        relevantProps = buckets.ready || [];
      else if (lowerMsg.includes('50,000') || lowerMsg.includes('50000'))
        relevantProps = properties.filter(p => (p.rba || 0) >= 50000);

      const propsToInclude = relevantProps.slice(0, 50);
      if (propsToInclude.length > 0 && propsToInclude.length < properties.length) {
        contextData = '\n\nRELEVANT PROPERTIES:\n' + propsToInclude.map(p =>
          `${p.address}, ${p.city || ''} ${p.state || ''} | ${p.rba || '?'} SF | Owner: ${p.owner_name || '?'} | Clear Ht: ${p.ceiling_ht || '?'} | Leased: ${p.percent_leased ?? '?'}%`
        ).join('\n');
      }

      // Include outreach data if asked about LOIs/outreach
      if (lowerMsg.includes('loi') || lowerMsg.includes('outreach') || lowerMsg.includes('sent') || lowerMsg.includes('response') || lowerMsg.includes('offer')) {
        const recentOutreach = outreachLog.slice(0, 30);
        if (recentOutreach.length > 0) {
          contextData += '\n\nRECENT OUTREACH:\n' + recentOutreach.map(o => {
            const prop = properties.find(p => p.id === o.property_id);
            return `${prop?.address || '?'} | $${o.loi_psf || '?'}/SF | $${o.loi_total?.toLocaleString() || '?'} total | ${o.sent_by} | ${o.status} | ${o.sent_at ? new Date(o.sent_at).toLocaleDateString() : '?'}`;
          }).join('\n');
        }
      }

      const systemPrompt = buildDbSystemPrompt() + contextData;
      const apiMessages = newMessages.filter(m => m.role !== 'system').slice(-20).map(m => ({ role: m.role, content: m.content }));
      const reply = await callClaude(systemPrompt, apiMessages);

      setDbChatMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setDbChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
    } finally {
      setDbChatLoading(false);
    }
  };

  // Scroll DB chat to bottom on new messages
  useEffect(() => {
    if (dbChatBottomRef.current) dbChatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [dbChatMessages, dbChatLoading]);

  const reloadAll = async () => {
    try {
      const [allProperties, allContacts, allOutreach, instOwners, settings] = await Promise.all([
        loadProperties(),
        loadContacts(),
        loadOutreachLog(),
        loadInstitutionalOwners(),
        loadAppSettings(),
      ]);
      setProperties(allProperties);
      setContacts(allContacts);
      setOutreachLog(allOutreach);
      setInstitutionalOwners(instOwners);
      setAppSettings(settings);
      const cats = categorizeProperties(allProperties, allContacts, allOutreach, instOwners, settings);
      setBuckets(cats);
    } catch (err) {
      console.error("Database load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Upload handler ──
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadStatus(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const parsed = parseCoStarExcel(workbook);

      if (parsed.length === 0) {
        setUploadStatus({ type: 'error', message: 'No valid properties found in file.' });
        setUploading(false);
        return;
      }

      const instNames = institutionalOwners.map(o => o.name.toLowerCase().trim());
      let created = 0, updated = 0;

      for (const prop of parsed) {
        const ownerLower = (prop.owner_name || '').toLowerCase().trim();
        let isInst = false;
        for (const inst of instNames) {
          if (ownerLower && (ownerLower.includes(inst) || inst.includes(ownerLower))) {
            isInst = true;
            break;
          }
        }
        if (isInst) prop.is_institutional = true;

        const result = await upsertProperty(prop);
        if (result.action === 'created') created++;
        else updated++;
      }

      setUploadStatus({ type: 'success', message: `Uploaded ${parsed.length} properties (${created} new, ${updated} updated)` });
      await reloadAll();
    } catch (err) {
      console.error("Upload error:", err);
      setUploadStatus({ type: 'error', message: `Upload failed: ${err.message}` });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Contact modal helpers ──
  const openAddContactModal = (prefill = {}) => {
    setContactFormData({
      name: '', title: '', owner_name: prefill.owner_name || '', emails: [''],
      phone: '', contact_type: prefill.contact_type || 'owner', property_id: prefill.property_id || '',
    });
    setContactModal({ mode: 'add' });
  };

  const openEditContactModal = (contact) => {
    // Hydrate the dynamic emails array from the legacy email1/2/3 columns,
    // plus any extras stored in email_extras (JSONB) if your DB has that column.
    const emails = [contact.email1, contact.email2, contact.email3, ...(Array.isArray(contact.email_extras) ? contact.email_extras : [])]
      .filter(e => e != null);
    setContactFormData({
      id: contact.id,
      name: contact.name || '',
      title: contact.title || '',
      owner_name: contact.owner_name || '',
      emails: emails.length > 0 ? emails : [''],
      phone: contact.phone || '',
      contact_type: contact.contact_type || 'owner',
      property_id: contact.property_id || '',
    });
    setContactModal({ mode: 'edit', contact });
  };

  const handleSaveContactModal = async () => {
    try {
      // Strip empty rows from the dynamic emails array before persisting.
      const cleanEmails = (contactFormData.emails || []).map(e => (e || '').trim()).filter(Boolean);
      const { emails, ...rest } = contactFormData;
      const payload = {
        ...rest,
        // Mirror first 3 to the existing schema columns. Extras (4+) go into
        // email_extras — a JSONB column you'll need to add to the contacts
        // table for the unlimited-CC behavior to actually persist beyond 3.
        email1: cleanEmails[0] || '',
        email2: cleanEmails[1] || '',
        email3: cleanEmails[2] || '',
        email_extras: cleanEmails.slice(3),
      };
      if (!payload.property_id) delete payload.property_id;
      // If email_extras is empty, don't send it — keeps things clean for
      // schemas that don't have the column yet.
      if (!payload.email_extras.length) delete payload.email_extras;
      await upsertContact(payload);
      await logActivity({ userName: currentUser.name, userEmail: currentUser.email, action: 'contact_added', description: `Contact ${payload.name} (${payload.email1}) added`, entityType: 'contact' });
      setContactModal(null);

      // Check for owner-name matches if no property_id
      if (!payload.property_id && payload.owner_name) {
        const matches = properties.filter(p =>
          p.owner_name && p.owner_name.toLowerCase().includes(payload.owner_name.toLowerCase())
        );
        if (matches.length > 0) {
          setToast({ type: 'info', message: `This contact matches ${matches.length} propert${matches.length === 1 ? 'y' : 'ies'} by owner name. They'll appear in the Ready bucket.` });
          setTimeout(() => setToast(null), 5000);
        }
      }

      await reloadAll();
    } catch (err) {
      console.error("Save contact error:", err);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.name}"? This cannot be undone.`)) return;
    try {
      await deleteContact(contact.id);
      await reloadAll();
    } catch (err) {
      console.error("Delete contact error:", err);
    }
  };

  // ── Property delete handlers ──
  // Note: `outreach_log` rows reference these properties via property_id but
  // we don't cascade — outreach history is preserved as a paper trail.
  // Old entries may show "Unknown Property" in the Outreach tab afterwards.
  const handleDeleteProperty = async (prop) => {
    const label = `${prop.address || 'this property'}${prop.city ? `, ${prop.city}` : ''}`;
    if (!window.confirm(`Delete "${label}"?\n\nPermanently removes it and blocks future re-imports of the same address. LOI history is kept.`)) return;
    try {
      await deleteProperties([prop.id]);
      // Drop it from selection too if it was selected
      setSelectedProps(prev => { const next = new Set(prev); next.delete(prop.id); return next; });
      await logActivity({ userName: currentUser.name, userEmail: currentUser.email, action: 'property_deleted', description: `Deleted ${label} from Sourcing`, entityType: 'property', entityId: prop.id });
      setToast({ type: 'success', message: 'Property deleted.' });
      setTimeout(() => setToast(null), 2500);
      await reloadAll();
    } catch (err) {
      console.error("Delete property error:", err);
      setToast({ type: 'error', message: `Failed to delete: ${err.message}` });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleDeleteSelectedProperties = async () => {
    const ids = [...selectedProps];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} propert${ids.length === 1 ? 'y' : 'ies'}?\n\nPermanently removes them and blocks future re-imports of the same addresses. LOI history is kept.`)) return;
    try {
      await deleteProperties(ids);
      setSelectedProps(new Set());
      setBatchPsf('');
      setToast({ type: 'success', message: `${ids.length} propert${ids.length === 1 ? 'y' : 'ies'} deleted.` });
      setTimeout(() => setToast(null), 2500);
      await reloadAll();
    } catch (err) {
      console.error("Bulk delete error:", err);
      setToast({ type: 'error', message: `Failed to delete: ${err.message}` });
      setTimeout(() => setToast(null), 4000);
    }
  };

  // Wipe every property in a single bucket (Ready / Missing / Recent / Institutional).
  // Deletes only what's currently visible in the bucket — e.g. respects the
  // active search filter so the user can preview-then-delete a subset.
  const handleDeleteBucket = async (bucketLabel, items) => {
    if (!items || items.length === 0) return;
    if (!window.confirm(`Delete all ${items.length} propert${items.length === 1 ? 'y' : 'ies'} in "${bucketLabel}"?\n\nPermanently removes them and blocks future re-imports of the same addresses. LOI history is kept.`)) return;
    try {
      const ids = items.map(p => p.id);
      await deleteProperties(ids);
      setSelectedProps(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
      setToast({ type: 'success', message: `${items.length} propert${items.length === 1 ? 'y' : 'ies'} deleted from ${bucketLabel}.` });
      setTimeout(() => setToast(null), 3000);
      await reloadAll();
    } catch (err) {
      console.error("Delete bucket error:", err);
      setToast({ type: 'error', message: `Failed to delete: ${err.message}` });
      setTimeout(() => setToast(null), 4000);
    }
  };

  // ── Upload Contacts from Excel ──
  const handleContactFileUpload = async (file) => {
    if (!file || contactUploading) return;
    setContactUploading(true);
    setContactUploadStatus("Reading file...");
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      if (rows.length === 0) { setContactUploadStatus("No rows found in file."); setContactUploading(false); return; }

      // Auto-detect column names (flexible mapping).
      //
      // Email column detection is deliberately loose so we don't drop CC-style
      // headers that don't literally say "email":
      //   HEADER match — any column whose normalized name contains "email",
      //                  "cc", "bcc", "recipient", or the words "alt"/
      //                  "additional"/"other" combined with a contact-y noun.
      //   VALUE sniff  — any remaining column whose FIRST non-empty cell
      //                  contains an @ and looks like an address. Catches
      //                  weirdly-named columns ("Contact 2", "R1", etc.)
      // Metadata columns (status/verified/valid/score) are excluded either way.
      // Gather columns as the UNION of every row's keys. SheetJS's
      // sheet_to_json only emits keys for cells that have values, so a
      // column that's blank on row 1 but populated on row 2 would be
      // invisible if we only looked at rows[0]. That was silently dropping
      // "Email #3+" in files where the first row didn't happen to fill them.
      const sheetKeys = Array.from(
        rows.reduce((set, r) => { Object.keys(r).forEach(k => set.add(k)); return set; }, new Set())
      );
      const norm = (k) => String(k).toLowerCase().replace(/[_\s#-]/g, '');
      const isMetaCol = (k) => {
        const n = norm(k);
        return n.includes('status') || n.includes('verified') || n.includes('verification') || n.includes('valid') || n.includes('score') || n === 'id' || n.includes('phone') || n.includes('mobile') || n.includes('cell') || n.includes('tel');
      };
      const headerLooksEmail = (k) => {
        const n = norm(k);
        if (n.includes('email')) return true;
        if (/^(cc|bcc)\d*$/.test(n)) return true;                       // "cc", "cc1", "bcc2"
        if (n === 'recipient' || /^recipient\d+$/.test(n)) return true;
        if (n === 'to' || /^to\d+$/.test(n)) return true;
        if (/^(alt|other|additional|secondary|tertiary)/.test(n) && (n.includes('addr') || n.includes('contact') || n.includes('mail'))) return true;
        return false;
      };
      const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const cellLooksEmail = (v) => {
        if (v == null) return false;
        const s = String(v).trim();
        return s.includes('@') && s.split(/[,;\s]+/).some(p => EMAIL_RE.test(p));
      };
      // For value-sniff we peek at up to 20 non-empty samples per column.
      const valueColLooksEmail = (col) => {
        let hits = 0, seen = 0;
        for (const row of rows) {
          const v = row[col];
          if (v == null || v === '') continue;
          seen++;
          if (cellLooksEmail(v)) hits++;
          if (seen >= 20) break;
        }
        return seen > 0 && hits / seen >= 0.5;   // at least half of samples parse as email
      };
      const emailCols = sheetKeys.filter(k => {
        if (isMetaCol(k)) return false;
        return headerLooksEmail(k) || valueColLooksEmail(k);
      });
      // Pull a trailing digit (or word "secondary"/"tertiary"/...) so we can
      // sort by intended order even when the headers aren't sequential.
      const ordinalWords = { primary: 1, secondary: 2, tertiary: 3, quaternary: 4, fifth: 5, sixth: 6 };
      const orderOf = (k) => {
        const n = norm(k);
        const digit = n.match(/(\d+)/);
        if (digit) return parseInt(digit[1], 10);
        for (const [w, i] of Object.entries(ordinalWords)) {
          if (n.includes(w)) return i;
        }
        return 100 + sheetKeys.indexOf(k);   // unranked → preserve sheet order, after ranked
      };
      const orderedEmailCols = [...emailCols].sort((a, b) => orderOf(a) - orderOf(b));

      const colMap = (row) => {
        const keys = Object.keys(row);
        const find = (terms) => keys.find(k => terms.some(t => k.toLowerCase().replace(/[_\s]/g, '').includes(t)));
        const cleanEmail = (v) => (v == null ? '' : String(v).trim().replace(/[,;]+/g, '').toLowerCase());
        // Pull every email-like cell, dedupe, drop blanks.
        const seen = new Set();
        const allEmails = [];
        for (const col of orderedEmailCols) {
          const raw = row[col];
          if (!raw) continue;
          // A single cell may contain multiple emails separated by , ; or whitespace.
          for (const part of String(raw).split(/[,;\s]+/)) {
            const e = cleanEmail(part);
            if (!e || !e.includes('@') || seen.has(e)) continue;
            seen.add(e);
            allEmails.push(e);
          }
        }
        return {
          owner_name: row[find(['owner', 'company', 'firm', 'entity'])] || '',
          name:       row[find(['contact', 'name', 'person', 'firstname'])] || row[find(['name'])] || '',
          title:      row[find(['title', 'role', 'position'])] || '',
          email1:     allEmails[0] || '',
          email2:     allEmails[1] || '',
          email3:     allEmails[2] || '',
          email_extras: allEmails.slice(3),
          phone:      row[find(['phone', 'tel', 'mobile', 'cell'])] || '',
          contact_type: row[find(['type', 'contacttype'])] || 'owner',
        };
      };

      const parsed = rows.map(colMap).filter(r => r.owner_name || r.name);
      // Diagnostic: show which columns were treated as email so a missed
      // header is instantly visible instead of silently dropped.
      const emailCounts = parsed.map(p => [p.email1, p.email2, p.email3, ...(p.email_extras || [])].filter(Boolean).length);
      const maxEmails = emailCounts.length ? Math.max(...emailCounts) : 0;
      const avgEmails = emailCounts.length ? (emailCounts.reduce((s, n) => s + n, 0) / emailCounts.length).toFixed(1) : 0;
      console.log("Contact upload — detected email columns:", orderedEmailCols);
      console.log(`Contact upload — max ${maxEmails} / avg ${avgEmails} emails per contact across ${parsed.length} rows`);
      setContactUploadStatus(`Parsed ${parsed.length} contacts (email cols: ${orderedEmailCols.join(", ") || "none!"} · avg ${avgEmails}/row · max ${maxEmails}). Verifying emails...`);

      let created = 0, updated = 0, trashed = 0, verified = 0;

      // Cache helpers — built once per upload, hit per row.
      const lc = (v) => String(v || '').toLowerCase().trim();
      // Map every existing contact's emails to the contact itself for fast
      // "do we already have this person?" lookup.
      const contactByEmail = new Map();
      for (const ex of contacts) {
        for (const e of [ex.email1, ex.email2, ex.email3, ...(Array.isArray(ex.email_extras) ? ex.email_extras : [])]) {
          if (e) contactByEmail.set(lc(e), ex);
        }
      }

      for (let i = 0; i < parsed.length; i++) {
        const c = parsed[i];

        // Match an existing contact, broadest to narrowest:
        //   1. Any incoming email matches any existing email   (strongest signal)
        //   2. owner_name + name both match                    (existing behavior)
        //   3. owner_name matches and no contact name on row   (single-contact-per-owner case)
        //   4. name matches and no owner_name on row           (loose fallback)
        let existing = null;
        const incomingEmails = [c.email1, c.email2, c.email3, ...(c.email_extras || [])].filter(Boolean);
        for (const e of incomingEmails) {
          const hit = contactByEmail.get(lc(e));
          if (hit) { existing = hit; break; }
        }
        if (!existing && c.owner_name && c.name) {
          existing = contacts.find(ex =>
            ex.owner_name && lc(ex.owner_name) === lc(c.owner_name) &&
            ex.name && lc(ex.name) === lc(c.name)
          );
        }
        if (!existing && c.owner_name && !c.name) {
          existing = contacts.find(ex => ex.owner_name && lc(ex.owner_name) === lc(c.owner_name));
        }
        if (!existing && c.name && !c.owner_name) {
          existing = contacts.find(ex => ex.name && lc(ex.name) === lc(c.name));
        }

        // Build contact object — upsertContact in supabase.js will auto-verify emails.
        // Merge new emails with the existing contact's emails (dedup, preserve
        // order) so a partial upload doesn't blank out emails the user added
        // manually before.
        const existingEmails = existing
          ? [existing.email1, existing.email2, existing.email3, ...(Array.isArray(existing.email_extras) ? existing.email_extras : [])]
              .filter(Boolean)
          : [];
        const newEmails = [c.email1, c.email2, c.email3, ...(c.email_extras || [])].filter(Boolean);
        const mergedSeen = new Set();
        const mergedEmails = [];
        for (const e of [...newEmails, ...existingEmails]) {
          const k = String(e).toLowerCase().trim();
          if (!k || mergedSeen.has(k)) continue;
          mergedSeen.add(k);
          mergedEmails.push(k);
        }
        const contactObj = {
          ...(existing ? { id: existing.id } : {}),
          owner_name: c.owner_name || existing?.owner_name || '',
          name: c.name || existing?.name || '',
          title: c.title || existing?.title || '',
          email1: mergedEmails[0] || '',
          email2: mergedEmails[1] || '',
          email3: mergedEmails[2] || '',
          email_extras: mergedEmails.slice(3),
          phone: c.phone || existing?.phone || '',
          contact_type: c.contact_type || existing?.contact_type || 'owner',
          property_id: existing?.property_id || null,
        };

        // Only set emails that have values
        if (!contactObj.email1) delete contactObj.email1;
        if (!contactObj.email2) delete contactObj.email2;
        if (!contactObj.email3) delete contactObj.email3;
        if (!contactObj.email_extras.length) delete contactObj.email_extras;

        try {
          const saved = await upsertContact(contactObj);
          if (existing) updated++;
          else created++;

          // Keep the email→contact lookup in sync with what we just wrote,
          // so a later row in the same upload that shares a NEW email with
          // this contact merges into it instead of creating a duplicate.
          for (const e of [saved.email1, saved.email2, saved.email3, ...(Array.isArray(saved.email_extras) ? saved.email_extras : [])]) {
            if (e) contactByEmail.set(lc(e), saved);
          }

          // Count trashed
          ['email1_status', 'email2_status', 'email3_status'].forEach(f => {
            if (saved[f] && ['invalid', 'spamtrap', 'abuse', 'do_not_mail'].includes(saved[f])) trashed++;
          });
          ['email1_status', 'email2_status', 'email3_status'].forEach(f => { if (saved[f]) verified++; });
        } catch (e) {
          console.error("Upload contact error:", e, contactObj);
        }

        if ((i + 1) % 5 === 0 || i === parsed.length - 1) {
          setContactUploadStatus(`Processing ${i + 1}/${parsed.length} — ${created} created, ${updated} updated, ${verified} verified, ${trashed} trashed`);
        }
      }

      // Auto-link new contacts to Missing Contact properties by owner_name
      setContactUploadStatus("Linking contacts to properties...");
      let linked = 0;
      const freshContacts = await loadContacts();
      const missingProps = (buckets.missing || []);
      for (const prop of missingProps) {
        if (!prop.owner_name) continue;
        const matchingContact = freshContacts.find(c =>
          c.owner_name && c.email1 &&
          c.owner_name.toLowerCase().trim() === prop.owner_name.toLowerCase().trim() &&
          !c.property_id
        );
        if (matchingContact) {
          await supabase.from('contacts').update({ property_id: prop.id }).eq('id', matchingContact.id);
          linked++;
        }
      }

      await reloadAll();
      setContactUploadStatus(`Done! ${created} created, ${updated} updated, ${verified} emails verified, ${trashed} trashed, ${linked} linked to properties.`);
      setTimeout(() => setContactUploadStatus(null), 8000);
    } catch (e) {
      console.error("Contact upload error:", e);
      setContactUploadStatus("Upload failed: " + e.message);
      setTimeout(() => setContactUploadStatus(null), 5000);
    } finally {
      setContactUploading(false);
      if (contactFileInputRef.current) contactFileInputRef.current.value = '';
    }
  };

  // ── LOI handlers ──
  // Returns the most-recent outreach for a property within the cooldown window,
  // or null if none. Used to warn the user before they double-LOI a property.
  const cooldownConflictFor = (propId) => {
    const cooldownDays = parseInt(appSettings.cooldown_days || '14', 10);
    if (!cooldownDays) return null;
    const cutoff = Date.now() - cooldownDays * 86400000;
    const prior = outreachLog
      .filter(o => o.property_id === propId && o.sent_at && new Date(o.sent_at).getTime() >= cutoff)
      .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))[0];
    if (!prior) return null;
    const days = Math.floor((Date.now() - new Date(prior.sent_at).getTime()) / 86400000);
    return { prior, days, cooldownDays };
  };

  // ── LOI pricing model (NNN ÷ YoC − CapEx) ─────────────────────────
  //
  //   Total Price per SF      = Market NNN Rent ÷ Year-3 Trended YoC
  //   Purchase Price per SF   = Total Price × (1 − CapEx ÷ Total Price)
  //                           = Total Price − CapEx
  //   Offer $ (per property)  = Purchase Price × RBA
  //
  // Global NNN + YoC live in app_settings; CapEx per SF lives on each
  // property row (properties.capex_per_sf). Explicit row PSF or a batch
  // PSF override both still short-circuit the formula so the team can
  // punch in a bespoke number when the formula doesn't apply.
  const getTotalPricePerSF = () => {
    const nnn = parseFloat(appSettings.market_nnn_rent);
    const yoc = parseFloat(appSettings.year3_trended_yoc);
    if (!nnn || !yoc || yoc <= 0) return null;
    return nnn / (yoc / 100);
  };

  const getPsfForProp = (prop) => {
    // 1. Explicit per-row typed override wins
    const explicit = psfValues[prop.id];
    if (explicit !== undefined && explicit !== '' && !isNaN(parseFloat(explicit))) return explicit;
    // 2. Formula-driven price
    const totalPrice = getTotalPricePerSF();
    if (totalPrice == null) return '';
    const capex = parseFloat(prop.capex_per_sf);
    if (isNaN(capex)) return String(Math.round(totalPrice * 100) / 100);   // no CapEx → total price is offer
    return String(Math.round((totalPrice - capex) * 100) / 100);
  };

  const openLoiPreview = (prop) => {
    const psf = parseFloat(getPsfForProp(prop));
    if (!psf || isNaN(psf)) return;
    const contact = prop.contact;
    const { body } = generateLoiBody(prop, contact, currentUser, psf, appSettings);
    const fullAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
    setLoiModal({
      prop,
      contact,
      psf,
      subject: `Off-Market Acquisition Interest — ${fullAddr}`,
      body,
      to: contact?.email1 || '',
      // CC = email2 + email3 + every entry in email_extras (the unlimited-CC column).
      cc: [contact?.email2, contact?.email3, ...(Array.isArray(contact?.email_extras) ? contact.email_extras : [])].filter(Boolean).join(', '),
      confirmStep: false,
      cooldownAck: false,
    });
  };

  // After a successful LOI send, either move an existing deal for this
  // property into "Unsolicited Offer" or create a new one. Returns the deal
  // (if created) so the toast can offer a "View deal →" hint.
  const promoteToUnsolicitedOffer = async (prop, outreachEntryId) => {
    // Deals raised by an LOI send are outreach — see the Outreach layer
    // group on the map, which is the same population.
    const STAGE = 'Sourcing';
    const norm = (s) => (s || '').toLowerCase().trim();
    const propAddr = norm(prop.address);

    // Query the DB directly instead of React state — during batch LOI sends,
    // the state is stale and misses deals created earlier in the same batch.
    const { data: dbDeals } = await supabase.from('deals').select('id, address, deal_stage').limit(500);
    let existing = null;
    for (const row of (dbDeals || [])) {
      let addrs = [];
      try { const p = JSON.parse(row.address || ''); addrs = Array.isArray(p) ? p : [row.address]; } catch { addrs = [row.address]; }
      const match = addrs.some(a => {
        const da = norm(a);
        return da.includes(propAddr) || propAddr.includes(da.split(',')[0]?.trim() || '');
      });
      if (match) { existing = { id: row.id, stage: row.deal_stage }; break; }
    }

    if (existing) {
      const ahead = ['Screening', 'Underwriting', 'LOI Submitted', 'In Diligence'];
      // Still link the outreach row to the deal even if we don't move stage,
      // so the deal_id chain is intact and downstream counters work.
      if (outreachEntryId) {
        await supabase.from('outreach_log').update({ deal_id: existing.id }).eq('id', outreachEntryId);
      }
      if (ahead.includes(existing.stage)) return null;
      if (existing.stage === STAGE || existing.stage === 'Outreach') return null;
      const { error } = await supabase
        .from('deals')
        .update({ deal_stage: STAGE })
        .eq('id', existing.id);
      if (error) { console.error("Promote stage error:", error); return null; }
      // Log the stage change so the weekly "LOIs Sent (Wk)" tile reflects
      // every promotion driven by a Sourcing LOI, not just manual moves.
      const dealDisplayAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
      await logActivity({
        userName: currentUser.name, userEmail: currentUser.email,
        action: 'stage_changed',
        description: `${dealDisplayAddr} moved from ${existing.stage || '?'} → ${STAGE}`,
        entityType: 'deal', entityId: existing.id,
        metadata: { old_stage: existing.stage, new_stage: STAGE, deal_name: dealDisplayAddr },
      });
      await autoIncrementTasks(currentUser.email, 'stage_changed');
      return null;
    }

    // Auto-create a deal in Unsolicited Offer stage.
    const fullAddr = [prop.address, prop.city || '', prop.state || ''].filter(Boolean).join(', ').replace(/,\s*$/, '');
    const dealData = {
      addresses: [fullAddr],
      market: prop.market || 'Other',
      squareFeet: prop.rba || 0,
      // deals.asking_price has a NOT NULL constraint; default to 0 when the
      // property has no sale price (matches the InboxView import path).
      askingPrice: prop.for_sale_price || 0,
      capRate: null,
      stage: STAGE,
      sourceNotes: buildPropertySourceNotes(prop, outreachLog),
      photoPath: '',
      thesis: '',
      openQuestions: '',
      redFlags: '',
    };
    const { data, error } = await supabase
      .from('deals')
      .insert([dealToDb(dealData)])
      .select()
      .single();
    if (error) {
      console.error("Auto-create deal error:", error);
      // Surface the failure so silent NOT-NULL / RLS errors can't hide a
      // broken sourcing → pipeline link the way they did on 2026-05-12.
      setToast({ type: 'error', message: `Auto-create deal failed for ${fullAddr}: ${error.message}` });
      setTimeout(() => setToast(null), 6000);
      return null;
    }
    // Geocode in the background — don't block.
    geocodeAddress(fullAddr).then(coords => {
      if (coords) supabase.from('deals').update({ latitude: coords.lat, longitude: coords.lng }).eq('id', data.id);
    });
    if (outreachEntryId) {
      await supabase.from('outreach_log').update({ deal_id: data.id }).eq('id', outreachEntryId);
    }
    // Log the new deal so the weekly "LOIs Sent (Wk)" tile includes auto-
    // created Unsolicited Offer pipeline entries from Sourcing LOIs.
    const dealDisplayAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
    await logActivity({
      userName: currentUser.name, userEmail: currentUser.email,
      action: 'deal_created',
      description: `${dealDisplayAddr} added to Pipeline (${STAGE}) from Sourcing LOI`,
      entityType: 'deal', entityId: data.id,
      metadata: { stage: STAGE, deal_name: dealDisplayAddr, source: 'sourcing_loi_promote' },
    });
    await autoIncrementTasks(currentUser.email, 'deal_created');
    const savedDeal = dbToDeal(data);
    onDealCreated?.(savedDeal);
    return savedDeal;
  };

  // Send the LOI via Microsoft Graph from the logged-in user's mailbox,
  // log the outreach entry attributed to them, and promote the property to
  // an Unsolicited Offer in the pipeline. If the send fails we do NOT log
  // outreach or touch the pipeline — keeps everything honest.
  const handleSendLoi = async () => {
    if (!loiModal) return;
    const { prop, contact, psf, subject, body, to, cc } = loiModal;
    if (!to) {
      setToast({ type: 'error', message: 'Recipient email is required.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    if (!subject?.trim()) {
      setToast({ type: 'error', message: 'Subject is required.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    // Recompute totals from the current PSF (body is editable but the math
    // we log to outreach_log should still reflect generated values).
    const { total, deposit, expiration } = generateLoiBody(prop, contact, currentUser, psf, appSettings);
    // Send the designed HTML email; keep the user-edited plain-text `body`
    // as the fallback content + the version we persist to outreach_log.
    const { html } = generateLoiHtml(prop, contact, currentUser, psf, appSettings);
    const ccList = (cc || '').split(',').map(s => s.trim()).filter(Boolean);

    setSendingLoi(true);
    try {
      await sendMail(msalInstance, { to, cc: ccList, subject, body, html });
      const newEntry = await createOutreachEntry({
        property_id: prop.id,
        contact_id: contact?.id || null,
        sent_by: currentUser.name,
        sent_at: new Date().toISOString(),
        loi_psf: psf,
        loi_total: total,
        deposit_amount: deposit,
        expiration_date: expiration,
        status: 'Sent',
        loi_body: body,
      });
      const createdDeal = await promoteToUnsolicitedOffer(prop, newEntry?.id);
      setLoiModal(null);
      const propAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
      await logActivity({
        userName: currentUser.name, userEmail: currentUser.email,
        action: 'loi_sent',
        description: `LOI sent to ${propAddr} at $${Number(psf).toLocaleString()}/SF`,
        entityType: 'outreach', entityId: newEntry?.id,
        metadata: { to, psf, total, address: propAddr },
      });
      await autoIncrementTasks(currentUser.email, 'loi_sent');
      setToast({
        type: 'success',
        message: createdDeal
          ? `LOI sent — new deal added to Pipeline as Unsolicited Offer.`
          : `LOI sent to ${to} from acquisitions@proximityparks.com.`,
      });
      setTimeout(() => setToast(null), 5000);
      await reloadAll();
    } catch (err) {
      console.error("Send LOI error:", err);
      setToast({ type: 'error', message: `Failed to send LOI: ${err.message}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setSendingLoi(false);
    }
  };

  // Escape hatch — log an outreach entry for an LOI that was sent manually
  // outside the app (e.g. user composed in Outlook directly). No email is sent.
  // Still promotes to Unsolicited Offer so the pipeline stays in sync.
  const handleLogAsSent = async () => {
    if (!loiModal) return;
    const { prop, contact, psf, body } = loiModal;
    const { total, deposit, expiration } = generateLoiBody(prop, contact, currentUser, psf, appSettings);
    try {
      const newEntry = await createOutreachEntry({
        property_id: prop.id,
        contact_id: contact?.id || null,
        sent_by: currentUser.name,
        sent_at: new Date().toISOString(),
        loi_psf: psf,
        loi_total: total,
        deposit_amount: deposit,
        expiration_date: expiration,
        status: 'Sent',
        loi_body: body,
      });
      await promoteToUnsolicitedOffer(prop, newEntry?.id);
      const propAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
      await logActivity({ userName: currentUser.name, userEmail: currentUser.email, action: 'loi_sent', description: `LOI logged as sent to ${propAddr} at $${Number(psf).toLocaleString()}/SF`, entityType: 'outreach', entityId: newEntry?.id });
      await autoIncrementTasks(currentUser.email, 'loi_sent');
      setLoiModal(null);
      setToast({ type: 'success', message: 'LOI logged as sent.' });
      setTimeout(() => setToast(null), 3000);
      await reloadAll();
    } catch (err) {
      console.error("Log LOI error:", err);
      setToast({ type: 'error', message: `Failed to log LOI: ${err.message}` });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleCopyLoi = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ type: 'success', message: 'Copied to clipboard!' });
      setTimeout(() => setToast(null), 3000);
    } catch {
      setToast({ type: 'error', message: 'Copy failed. Try selecting manually.' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  // ── View email conversation for an outreach entry ──
  // Searches the shared acquisitions mailbox for messages to/from the
  // contact's email, finds the matching conversation thread, and opens
  // it in a modal. Falls back to a "no emails found" notice.
  const openConversation = async (entry) => {
    const contact = contacts.find(c => c.id === entry.contact_id);
    const contactEmail = contact?.email1;
    if (!contactEmail) {
      setToast({ type: 'error', message: 'No email on file for this contact.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setConversationModal({ entry, thread: [], loading: true });
    try {
      // Search inbox for messages from this contact
      const { messages } = await fetchInboxMessages(msalInstance, {
        top: 50,
        filter: `from/emailAddress/address eq '${contactEmail}'`,
      });
      if (messages.length > 0) {
        // Get the full conversation thread for the most recent matching message
        const latest = messages[0];
        const thread = await fetchConversationThread(msalInstance, latest.conversationId);
        setConversationModal(prev => prev ? { ...prev, thread: thread.length > 0 ? thread : [latest], loading: false } : null);
      } else {
        // Also search sent items (our outgoing LOI might be the only message)
        setConversationModal(prev => prev ? { ...prev, thread: [], loading: false } : null);
      }
    } catch (e) {
      console.error('Conversation fetch error:', e);
      setConversationModal(prev => prev ? { ...prev, loading: false, error: e.message } : null);
    }
  };

  // ── Bulk selection helpers ──
  const togglePropSelection = (propId) => {
    setSelectedProps(prev => {
      const next = new Set(prev);
      if (next.has(propId)) next.delete(propId); else next.add(propId);
      return next;
    });
  };

  const toggleSelectAll = (items) => {
    const allIds = items.map(p => p.id);
    const allSelected = allIds.every(id => selectedProps.has(id));
    if (allSelected) {
      setSelectedProps(prev => { const next = new Set(prev); allIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedProps(prev => { const next = new Set(prev); allIds.forEach(id => next.add(id)); return next; });
    }
  };

  // ── Portfolio LOI handler ──
  // If `psf` is set: that single PSF applies to every property (override).
  // If `psf` is empty: fall back to each property's class-default PSF, then
  // compute a weighted average PSF per owner group so the portfolio LOI
  // still has one offer line but reflects class-based pricing.
  const openPortfolioLoi = (props, contact, psf) => {
    const explicitPsf = psf && !isNaN(parseFloat(psf)) ? parseFloat(psf) : null;
    // Group by owner
    const ownerGroups = {};
    for (const p of props) {
      const owner = p.owner_name || 'Unknown';
      if (!ownerGroups[owner]) ownerGroups[owner] = { owner, props: [], contact: p.contact || contact };
      ownerGroups[owner].props.push(p);
    }
    // New pricing chain: batch override → per-row override → NNN÷YoC − CapEx
    // formula. Returns null if none produce a valid PSF.
    const totalPrice = getTotalPricePerSF();
    const resolvePsf = (p) => {
      if (explicitPsf != null) return explicitPsf;
      const rowOverride = psfValues[p.id];
      if (rowOverride !== undefined && rowOverride !== '' && !isNaN(parseFloat(rowOverride))) {
        return parseFloat(rowOverride);
      }
      if (totalPrice == null) return null;
      const capex = parseFloat(p.capex_per_sf);
      const purchase = isNaN(capex) ? totalPrice : (totalPrice - capex);
      return purchase > 0 ? purchase : null;
    };
    // Per group: total offer = sum(rba × resolved psf); weighted PSF = total / totalSF.
    const groups = [];
    for (const g of Object.values(ownerGroups)) {
      let totalSf = 0, totalDollars = 0, missing = false;
      for (const p of g.props) {
        const sf = Number(p.rba) || 0;
        const ppsf = resolvePsf(p);
        if (!ppsf) { missing = true; break; }
        totalSf += sf;
        totalDollars += sf * ppsf;
      }
      if (missing || totalSf <= 0) continue; // skip groups we can't price
      groups.push({ ...g, psf: Math.round(totalDollars / totalSf) });
    }
    if (groups.length === 0) return;
    setPortfolioLoiModal({ groups });
    setPortfolioEdits({});   // clear any leftover edits from a previous batch
  };

  // Batch-LOI gate. Enabled when every selected property has SOME PSF
  // resolvable: per-row override, batch-PSF override, or the NNN÷YoC
  // formula (which requires both NNN and YoC set at the top; CapEx is
  // optional per row — missing CapEx just means the property's offer is
  // the full Total Price).
  const canBatchLoi = (selItems) => {
    if (!selItems || selItems.length === 0) return false;
    const hasBatch = batchPsf && !isNaN(parseFloat(batchPsf));
    const totalPrice = getTotalPricePerSF();
    return selItems.every(p => {
      const perRow = psfValues[p.id];
      if (perRow !== undefined && perRow !== '' && !isNaN(parseFloat(perRow))) return true;
      if (hasBatch) return true;
      if (totalPrice == null) return false;
      const capex = parseFloat(p.capex_per_sf);
      const purchase = isNaN(capex) ? totalPrice : (totalPrice - capex);
      return purchase > 0;
    });
  };

  // Human-readable explanation of why the batch button is disabled — surfaced
  // as a title on the button so the user can fix the blocking rows.
  const batchLoiBlockedReason = (selItems) => {
    if (!selItems || selItems.length === 0) return "Select properties to enable batch LOI.";
    const hasBatch = batchPsf && !isNaN(parseFloat(batchPsf));
    if (hasBatch) return "";
    const totalPrice = getTotalPricePerSF();
    if (totalPrice == null) return "Set Market NNN Rent and Year-3 Trended YoC at the top so the formula can price selected rows.";
    const unpriced = selItems.filter(p => {
      const perRow = psfValues[p.id];
      if (perRow !== undefined && perRow !== '' && !isNaN(parseFloat(perRow))) return false;
      const capex = parseFloat(p.capex_per_sf);
      const purchase = isNaN(capex) ? totalPrice : (totalPrice - capex);
      return !(purchase > 0);
    });
    if (unpriced.length === 0) return "";
    const sample = unpriced.slice(0, 3).map(p => p.address || '(no address)').join('; ');
    const more = unpriced.length > 3 ? ` (+${unpriced.length - 3} more)` : '';
    return `${unpriced.length} selected propert${unpriced.length === 1 ? 'y has' : 'ies have'} no PSF available — set a Classification, a per-row $/SF, or an Override $/SF for: ${sample}${more}`;
  };

  // Send each group's LOI via Graph; only log outreach for groups that sent
  // successfully. Track failures so the user can retry.
  const handleSendPortfolioLois = async () => {
    if (!portfolioLoiModal) return;
    setSendingLoi(true);
    const failures = [];
    let successCount = 0;
    try {
      for (let gi = 0; gi < portfolioLoiModal.groups.length; gi++) {
        const group = portfolioLoiModal.groups[gi];
        const edit = portfolioEdits[gi] || {};
        const isMulti = group.props.length > 1;
        const genFn = isMulti ? generatePortfolioLoiBody : generateLoiBody;
        const genHtmlFn = isMulti ? generatePortfolioLoiHtml : generateLoiHtml;
        const genArgs = isMulti
          ? [group.props, group.contact, currentUser, group.psf, appSettings]
          : [group.props[0], group.contact, currentUser, group.psf, appSettings];
        const { body: defaultBody, total, deposit, expiration } = genFn(...genArgs);
        const { html: defaultHtml } = genHtmlFn(...genArgs);

        // Edits override the generated defaults. If the user changed the body,
        // we send the edited plain text in BOTH the text and html positions
        // (the html version of the user's freeform edit is just a <pre>-style
        // wrap so it survives Outlook's reflow).
        const body = edit.body != null ? edit.body : defaultBody;
        const html = edit.body != null
          ? `<div style="font-family:Calibri,Arial,sans-serif;font-size:11pt;white-space:pre-wrap">${edit.body.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>`
          : defaultHtml;
        const defaultSubject = isMulti
          ? `Off-Market Portfolio Acquisition Interest — ${group.props.length} properties`
          : `Off-Market Acquisition Interest — ${[group.props[0].address, group.props[0].city, group.props[0].state].filter(Boolean).join(', ')}`;
        const subject = edit.subject != null ? edit.subject : defaultSubject;

        const toEmail = edit.to != null ? edit.to.trim() : (group.contact?.email1 || '');
        if (!toEmail) {
          failures.push(`${group.owner} (no email)`);
          continue;
        }
        const defaultCc = [group.contact?.email2, group.contact?.email3, ...(Array.isArray(group.contact?.email_extras) ? group.contact.email_extras : [])].filter(Boolean);
        const cc = edit.cc != null
          ? edit.cc.split(',').map(s => s.trim()).filter(Boolean)
          : defaultCc;

        try {
          await sendMail(msalInstance, { to: toEmail, cc, subject, body, html });
          for (const prop of group.props) {
            const newEntry = await createOutreachEntry({
              property_id: prop.id,
              contact_id: group.contact?.id || null,
              sent_by: currentUser.name,
              sent_at: new Date().toISOString(),
              loi_psf: group.psf,
              loi_total: group.props.length > 1 ? Math.round(group.psf * (prop.rba || 0)) : total,
              deposit_amount: deposit,
              expiration_date: expiration,
              status: 'Sent',
              loi_body: body,
            });
            // Promote each property to Unsolicited Offer in the pipeline.
            await promoteToUnsolicitedOffer(prop, newEntry?.id);
          }
          successCount++;
          // Log each property in the group
          for (const prop of group.props) {
            const propAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
            await logActivity({ userName: currentUser.name, userEmail: currentUser.email, action: 'loi_sent', description: `LOI sent to ${propAddr} at $${Number(group.psf).toLocaleString()}/SF`, entityType: 'outreach' });
            await autoIncrementTasks(currentUser.email, 'loi_sent');
          }
        } catch (sendErr) {
          console.error(`Send failed for ${group.owner}:`, sendErr);
          failures.push(`${group.owner} (${sendErr.message})`);
        }
      }
      setPortfolioLoiModal(null);
      setSelectedProps(new Set());
      setBatchPsf('');
      if (failures.length === 0) {
        setToast({ type: 'success', message: `${successCount} LOI${successCount === 1 ? '' : 's'} sent from acquisitions@proximityparks.com.` });
      } else {
        setToast({ type: 'error', message: `${successCount} sent, ${failures.length} failed: ${failures.join('; ')}` });
      }
      setTimeout(() => setToast(null), 6000);
      await reloadAll();
    } finally {
      setSendingLoi(false);
    }
  };

  // Escape hatch — log portfolio LOIs as sent without actually sending.
  const handleLogPortfolioAsSent = async () => {
    if (!portfolioLoiModal) return;
    try {
      for (let gi = 0; gi < portfolioLoiModal.groups.length; gi++) {
        const group = portfolioLoiModal.groups[gi];
        const edit = portfolioEdits[gi] || {};
        const genFn = group.props.length > 1 ? generatePortfolioLoiBody : generateLoiBody;
        const genArgs = group.props.length > 1
          ? [group.props, group.contact, currentUser, group.psf, appSettings]
          : [group.props[0], group.contact, currentUser, group.psf, appSettings];
        const { body: defaultBody, total, deposit, expiration } = genFn(...genArgs);
        const body = edit.body != null ? edit.body : defaultBody;

        for (const prop of group.props) {
          const newEntry = await createOutreachEntry({
            property_id: prop.id,
            contact_id: group.contact?.id || null,
            sent_by: currentUser.name,
            sent_at: new Date().toISOString(),
            loi_psf: group.psf,
            loi_total: group.props.length > 1 ? Math.round(group.psf * (prop.rba || 0)) : total,
            deposit_amount: deposit,
            expiration_date: expiration,
            status: 'Sent',
            loi_body: body,
          });
          await promoteToUnsolicitedOffer(prop, newEntry?.id);
          const propAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
          await logActivity({ userName: currentUser.name, userEmail: currentUser.email, action: 'loi_sent', description: `LOI logged as sent to ${propAddr} at $${Number(group.psf).toLocaleString()}/SF`, entityType: 'outreach', entityId: newEntry?.id });
          await autoIncrementTasks(currentUser.email, 'loi_sent');
        }
      }
      setPortfolioLoiModal(null);
      setSelectedProps(new Set());
      setBatchPsf('');
      const totalProps = portfolioLoiModal.groups.reduce((s, g) => s + g.props.length, 0);
      setToast({ type: 'success', message: `${totalProps} LOIs logged as sent across ${portfolioLoiModal.groups.length} owner(s).` });
      setTimeout(() => setToast(null), 4000);
      await reloadAll();
    } catch (err) {
      console.error("Portfolio LOI error:", err);
      setToast({ type: 'error', message: `Failed: ${err.message}` });
      setTimeout(() => setToast(null), 3000);
    }
  };

  // ── Outreach handlers ──
  const handleUpdateOutreachStatus = async (entry, newStatus) => {
    try {
      await updateOutreachEntry(entry.id, { status: newStatus });
      await reloadAll();
    } catch (err) {
      console.error("Update outreach error:", err);
    }
  };

  const handleUpdateOutreachResponse = async (entry, response) => {
    try {
      await updateOutreachEntry(entry.id, { response });
    } catch (err) {
      console.error("Update response error:", err);
    }
  };

  // ── Create Deal from property ──
  const isPropertyInPipeline = (prop) => {
    if (!prop?.address) return false;
    const propAddr = prop.address.toLowerCase().trim();
    return deals.some(d => {
      const dealAddrs = d.addresses || [];
      return dealAddrs.some(a => {
        const da = a.toLowerCase().trim();
        return da.includes(propAddr) || propAddr.includes(da.split(',')[0]?.trim());
      });
    });
  };

  const handleCreateDeal = async () => {
    if (!createDealModal || createDealSaving) return;
    setCreateDealSaving(true);
    const prop = createDealModal;
    try {
      const fullAddr = [prop.address, prop.city || '', prop.state || ''].filter(Boolean).join(', ').replace(/,\s*$/, '');
      const dealData = {
        addresses: [fullAddr],
        market: createDealMarket || prop.market || 'Other',
        squareFeet: prop.rba || 0,
        askingPrice: prop.for_sale_price || null,
        capRate: null,
        stage: createDealStage,
        sourceNotes: buildPropertySourceNotes(prop, outreachLog),
        photoPath: '',
        thesis: '',
        openQuestions: '',
        redFlags: '',
      };

      const { data, error } = await supabase
        .from('deals')
        .insert([dealToDb(dealData)])
        .select()
        .single();

      if (error) throw error;

      // Auto-geocode the new deal
      const coords = await geocodeAddress(fullAddr);
      if (coords) {
        await supabase.from('deals').update({ latitude: coords.lat, longitude: coords.lng }).eq('id', data.id);
      }

      const savedDeal = dbToDeal(data);

      // Link outreach_log entries to the new deal
      await supabase
        .from('outreach_log')
        .update({ deal_id: savedDeal.id })
        .eq('property_id', prop.id);

      setCreateDealModal(null);
      onDealCreated?.(savedDeal);
      setToast({ type: 'success', message: 'Deal created! View it in the Pipeline tab.' });
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      console.error("Create deal error:", err);
      setToast({ type: 'error', message: `Failed to create deal: ${err.message}` });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setCreateDealSaving(false);
    }
  };

  // ── Owner name autocomplete suggestions ──
  const ownerSuggestions = contactFormData.owner_name.length >= 2
    ? [...new Set(properties.map(p => p.owner_name).filter(Boolean))]
        .filter(name => name.toLowerCase().includes(contactFormData.owner_name.toLowerCase()))
        .slice(0, 5)
    : [];

  // ── Contact count for a property ──
  const contactCountFor = (propId) => contacts.filter(c => c.property_id === propId).length;

  // ── Search filter for properties ──
  const filterBucket = (items) => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(p =>
      (p.address || '').toLowerCase().includes(term) ||
      (p.city || '').toLowerCase().includes(term) ||
      (p.state || '').toLowerCase().includes(term) ||
      (p.owner_name || '').toLowerCase().includes(term) ||
      (p.market || '').toLowerCase().includes(term)
    );
  };

  // ── Search filter for contacts ──
  // Trashed statuses — emails with these are hidden from contact cards
  const TRASHED_STATUSES = new Set(["invalid", "spamtrap", "abuse", "do_not_mail"]);

  // Count email statuses — separate visible (on cards) vs trashed
  const emailStatusCounts = { valid: 0, "catch-all": 0, unknown: 0, disposable: 0, unverified: 0, trashed: 0, needs_update: 0, missing_contact: 0 };
  contacts.forEach(c => {
    [c.email1_status, c.email2_status, c.email3_status].forEach((s, i) => {
      const hasEmail = [c.email1, c.email2, c.email3][i];
      if (!hasEmail) return;
      if (TRASHED_STATUSES.has(s)) { emailStatusCounts.trashed++; return; }
      if (!s) emailStatusCounts.unverified++;
      else if (emailStatusCounts[s] !== undefined) emailStatusCounts[s]++;
    });
  });

  // Collect trashed emails for the Trashed view
  // Collect trashed emails
  const trashedEmails = [];
  const trashedStatusCounts = { invalid: 0, spamtrap: 0, abuse: 0, do_not_mail: 0 };
  contacts.forEach(c => {
    [["email1", c.email1, c.email1_status], ["email2", c.email2, c.email2_status], ["email3", c.email3, c.email3_status]].forEach(([field, email, status]) => {
      if (email && TRASHED_STATUSES.has(status)) {
        trashedEmails.push({ contact: c, email, status, field });
        if (trashedStatusCounts[status] !== undefined) trashedStatusCounts[status]++;
      }
    });
  });

  // Count contacts needing email update or missing contact entirely
  contacts.forEach(c => {
    const visible = [
      c.email1 && !TRASHED_STATUSES.has(c.email1_status) ? c.email1 : null,
      c.email2 && !TRASHED_STATUSES.has(c.email2_status) ? c.email2 : null,
      c.email3 && !TRASHED_STATUSES.has(c.email3_status) ? c.email3 : null,
    ].filter(Boolean);
    const hasValid = [c.email1_status, c.email2_status, c.email3_status].some(s => s === "valid");
    if (visible.length === 0) emailStatusCounts.missing_contact++;
    else if (!hasValid) emailStatusCounts.needs_update++;
  });

  // Filter trashed emails by status filter
  const filteredTrashed = emailStatusFilter
    ? trashedEmails.filter(t => t.status === emailStatusFilter)
    : trashedEmails;

  // Helper: does a contact have at least one visible email matching the status filter?
  function contactMatchesStatusFilter(c) {
    if (!emailStatusFilter) return true;
    if (emailStatusFilter === "missing_contact") {
      const visible = [
        c.email1 && !TRASHED_STATUSES.has(c.email1_status) ? c.email1 : null,
        c.email2 && !TRASHED_STATUSES.has(c.email2_status) ? c.email2 : null,
        c.email3 && !TRASHED_STATUSES.has(c.email3_status) ? c.email3 : null,
      ].filter(Boolean);
      return visible.length === 0;
    }
    if (emailStatusFilter === "needs_update") {
      return (c.email1 || c.email2 || c.email3) && ![c.email1_status, c.email2_status, c.email3_status].some(s => s === "valid");
    }
    if (emailStatusFilter === "unverified") {
      return (c.email1 && !c.email1_status && !TRASHED_STATUSES.has(c.email1_status)) ||
             (c.email2 && !c.email2_status && !TRASHED_STATUSES.has(c.email2_status)) ||
             (c.email3 && !c.email3_status && !TRASHED_STATUSES.has(c.email3_status));
    }
    return (c.email1 && c.email1_status === emailStatusFilter && !TRASHED_STATUSES.has(c.email1_status)) ||
           (c.email2 && c.email2_status === emailStatusFilter && !TRASHED_STATUSES.has(c.email2_status)) ||
           (c.email3 && c.email3_status === emailStatusFilter && !TRASHED_STATUSES.has(c.email3_status));
  }

  const filteredContacts = contacts.filter(c => {
    if (contactSearch.trim()) {
      const term = contactSearch.toLowerCase();
      const matchesSearch = (c.name || '').toLowerCase().includes(term) ||
        (c.email1 || '').toLowerCase().includes(term) ||
        (c.owner_name || '').toLowerCase().includes(term) ||
        (c.phone || '').toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    if (emailStatusFilter && !showTrashedEmails) return contactMatchesStatusFilter(c);
    return true;
  });

  const toggleBucket = (key) => setExpandedBuckets(prev => ({ ...prev, [key]: !prev[key] }));

  const fmtNum = (n) => n != null ? Number(n).toLocaleString() : '—';
  const fmtPct = (n) => n != null ? `${Number(n).toFixed(1)}%` : '—';

  const contactTypeBadge = (type) => {
    const colors = { owner: 'bg-[#4a4a4a]', leasing: 'bg-[#4a4a4a]', broker: 'bg-[#4a4a4a]' };
    return <span className={`${colors[type] || 'bg-[#4a4a4a]'} text-xs px-2 py-0.5 rounded-full capitalize`} style={{ color: '#ffffff' }}>{type || 'unknown'}</span>;
  };

  // ── Email verification status badge ──
  const EmailWithStatus = ({ email, status, showTrashed = false }) => {
    if (!email) return null;
    // Hide trashed emails from normal view (they appear in Trashed Emails section)
    if (!showTrashed && TRASHED_STATUSES.has(status)) return null;
    const config = {
      valid:       { icon: "\u2705", label: "Valid",       cls: "text-[#4f9d76]" },
      invalid:     { icon: "\u274C", label: "Invalid",     cls: "text-[#f04f44]" },
      "catch-all": { icon: "\u26A0\uFE0F", label: "Catch-All",  cls: "text-[#c8922f]" },
      unknown:     { icon: "\u2754", label: "Unknown",     cls: "text-[#8a8a8a]" },
      spamtrap:    { icon: "\uD83D\uDEAB", label: "Spam Trap",  cls: "text-[#e93529]" },
      abuse:       { icon: "\uD83D\uDEAB", label: "Abuse",      cls: "text-[#e93529]" },
      do_not_mail: { icon: "\uD83D\uDEAB", label: "Do Not Mail",cls: "text-[#f04f44]" },
      disposable:  { icon: "\uD83D\uDDD1\uFE0F", label: "Disposable", cls: "text-[#c8922f]" },
    };
    const c = config[status] || null;
    return (
      <span className="inline-flex items-center gap-1">
        <a href={`mailto:${email}`} className="text-[#f04f44] hover:text-[#f04f44]">{email}</a>
        {c && <span className={`text-xs ${c.cls}`} title={c.label}>{c.icon}</span>}
        {!status && <span className="text-xs text-[#7a7a7a]" title="Not verified">—</span>}
      </span>
    );
  };

  // ── Contact count badge for property cards ──
  const ContactCountBadge = ({ propId, bucket }) => {
    const count = contactCountFor(propId);
    if (count > 0) return <span className="text-[#8a8a8a] text-xs">{count} contact{count !== 1 ? 's' : ''}</span>;
    if (bucket !== 'missing') return <span className="text-[#8a8a8a] text-xs">No contacts</span>;
    return null;
  };

  // ── Property row renderer ──
  const classColors = { 1: "#4f9d76", 2: "#c8922f", 3: "#e93529" };
  const classRowBg = { 1: "rgba(79,157,118,0.08)", 2: "rgba(200,146,47,0.08)", 3: "rgba(233,53,41,0.08)" };
  const classRowBorder = { 1: "rgba(79,157,118,0.25)", 2: "rgba(200,146,47,0.25)", 3: "rgba(233,53,41,0.25)" };
  const PropertyRow = ({ prop, extra, bucket }) => {
    const inPipeline = isPropertyInPipeline(prop);
    const cls = prop.classification;
    return (
      <div className="rounded-lg p-4 mb-2 transition-colors" style={cls ? { background: classRowBg[cls], border: `1px solid ${classRowBorder[cls]}` } : { background: '#ffffff', border: '1px solid #e4e4e4' }}>
        <div className="grid gap-4 items-center" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 2fr 1fr 1fr 1fr 1fr 1fr" }}>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-white truncate">{prop.address}</p>
              {inPipeline && <span className="shrink-0 bg-[#44906a] text-white text-xs px-2 py-0.5 rounded-full">In Pipeline</span>}
            </div>
            <p className="text-[#8a8a8a] text-xs">{[prop.city, prop.state].filter(Boolean).join(', ')}</p>
            <ContactCountBadge propId={prop.id} bucket={bucket} />
          </div>
          <div onClick={e => e.stopPropagation()}>
            <select
              value={prop.classification || ""}
              onChange={async (e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                try {
                  await updateProperty(prop.id, { classification: val });
                  await reloadAll();
                } catch (err) { console.error("Classification update:", err); }
              }}
              className="bg-[#3a3a3a] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-pp-red w-full"
              style={prop.classification ? { borderColor: classColors[prop.classification], borderWidth: 2 } : {}}
            >
              <option value="">—</option>
              <option value="1">1 (Best)</option>
              <option value="2">2</option>
              <option value="3">3 (Low)</option>
            </select>
          </div>
          <div className="text-sm text-[#b4b4b4]">{prop.rba ? `${fmtNum(prop.rba)} SF` : '—'}</div>
          <div className="text-sm text-[#b4b4b4]">{prop.year_built || '—'}</div>
          <div className="text-sm text-[#b4b4b4] truncate">{prop.owner_name || '—'}</div>
          <div className="text-sm text-[#b4b4b4]">{prop.ceiling_ht || '—'}</div>
          <div className="text-sm text-[#b4b4b4]">{prop.loading_docks != null ? prop.loading_docks : '—'}</div>
          <div className="text-sm text-[#b4b4b4] truncate">{prop.for_sale_status || '—'}</div>
          <div className="text-sm text-[#b4b4b4]">{fmtPct(prop.percent_leased)}</div>
          <div className="flex justify-end items-center gap-1">
            {!inPipeline && (
              <button
                onClick={() => { setCreateDealStage('Screening'); setCreateDealMarket(prop.market || ''); setCreateDealModal(prop); }}
                className="text-xs bg-[#44906a] hover:bg-[#4f9d76] text-white px-2 py-1 rounded transition-colors whitespace-nowrap"
              >
                + Deal
              </button>
            )}
            <button
              onClick={() => handleDeleteProperty(prop)}
              className="text-[#8a8a8a] hover:text-[#f04f44] transition-colors p-1 rounded"
              title="Delete property"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
            </button>
          </div>
        </div>
        {extra}
      </div>
    );
  };

  // ── Column header ──
  const ColumnHeader = () => (
    <div className="grid grid-cols-13 gap-4 px-4 pb-2 text-xs text-[#8a8a8a] uppercase tracking-wider" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 2fr 1fr 1fr 1fr 1fr 1fr" }}>
      <div>Address</div>
      <div>CapEx $/SF</div>
      <div>SF</div>
      <div>Year</div>
      <div>Owner</div>
      <div>Clear Ht</div>
      <div>Docks</div>
      <div>For Sale</div>
      <div>Leased</div>
      <div></div>
    </div>
  );

  // Bundle props passed to the module-scoped <BucketSectionStable> at every
  // call site. Defined as a regular const (not a component) so it doesn't
  // create a new component identity on each render.
  const bucketDeps = {
    filterBucket,
    expandedBuckets,
    toggleBucket,
    handleDeleteBucket,
    searchTerm,
    ColumnHeader,
    PropertyRow,
  };

  // ContactModal JSX is rendered inline (see the main return) so the input
  // doesn't lose focus on every keystroke. The previous ContactModal-as-
  // component pattern caused React to remount on every contactFormData
  // update, which dropped focus after one character.

  if (loading) {
    return (
      <div className="text-center py-24 text-[#8a8a8a]">
        <div className="flex justify-center gap-1.5 mb-4">
          <span className="w-2 h-2 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
        <p className="text-sm">Loading sourcing data…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0">
      {/* ── Main content ── */}
      <div className={`flex-1 transition-all duration-300 ${dbChatOpen ? 'mr-[400px]' : ''}`}>
        <div className="max-w-7xl mx-auto">
      {/* ── Contact Modal (light/branded) ── */}
      {contactModal && (() => {
        const isEdit = contactModal.mode === 'edit';
        // Hide owner suggestions that exactly match the current value to
        // avoid the "duplicate Owner Name field" appearance.
        const visibleOwnerSuggestions = ownerSuggestions.filter(n => n.toLowerCase() !== contactFormData.owner_name.toLowerCase());
        const setEmailAt = (i, v) => setContactFormData(f => {
          const next = [...(f.emails || [''])];
          next[i] = v;
          return { ...f, emails: next };
        });
        const addEmail = () => setContactFormData(f => ({ ...f, emails: [...(f.emails || []), ''] }));
        const removeEmailAt = (i) => setContactFormData(f => {
          const next = (f.emails || []).filter((_, idx) => idx !== i);
          return { ...f, emails: next.length ? next : [''] };
        });
        const inputCls = "w-full bg-[#2c2c2c] border border-[#3a3a3a] text-[#f4f4f4] rounded-lg px-3 py-2 text-sm placeholder:text-[#8a8a8a] focus:outline-none focus:ring-2 focus:ring-pp-red/40 focus:border-pp-red transition-colors";
        const labelCls = "text-xs font-semibold text-[#7a7a7a] mb-1 block uppercase tracking-wide";
        const canSave = !!contactFormData.name && !!(contactFormData.emails || [])[0];

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setContactModal(null)}>
            <div className="bg-[#242424] text-[#f4f4f4] rounded-xl p-6 w-full max-w-lg shadow-2xl border border-[#3a3a3a] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-5">{isEdit ? 'Edit Contact' : 'Add Contact'}</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Name *</label>
                    <input value={contactFormData.name} onChange={e => setContactFormData(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Full name" />
                  </div>
                  <div>
                    <label className={labelCls}>Title</label>
                    <input value={contactFormData.title} onChange={e => setContactFormData(f => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="e.g. Vice President" />
                  </div>
                </div>

                <div className="relative">
                  <label className={labelCls}>Owner Name</label>
                  <input value={contactFormData.owner_name} onChange={e => setContactFormData(f => ({ ...f, owner_name: e.target.value }))} className={inputCls} placeholder="Match to property owner" />
                  {visibleOwnerSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      <div className="px-3 py-1.5 text-[10px] font-bold text-[#8a8a8a] uppercase tracking-wide bg-[#242424] border-b border-[#3a3a3a]">Suggestions</div>
                      {visibleOwnerSuggestions.map(name => (
                        <button key={name} onClick={() => setContactFormData(f => ({ ...f, owner_name: name }))} className="w-full text-left px-3 py-1.5 text-sm text-pp-dark hover:bg-[#ebebeb] truncate">{name}</button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dynamic emails list */}
                <div>
                  <label className={labelCls}>Emails</label>
                  <div className="space-y-2">
                    {(contactFormData.emails || ['']).map((email, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-[#8a8a8a] uppercase tracking-wide w-12 shrink-0">
                          {i === 0 ? 'TO' : `CC ${i}`}
                        </span>
                        <input
                          type="email"
                          value={email}
                          onChange={e => setEmailAt(i, e.target.value)}
                          className={inputCls + ' flex-1'}
                          placeholder={i === 0 ? 'primary@example.com' : 'cc@example.com'}
                        />
                        {(contactFormData.emails || []).length > 1 && (
                          <button
                            onClick={() => removeEmailAt(i)}
                            className="text-[#8a8a8a] hover:text-[#e93529] transition-colors p-1"
                            title="Remove email"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={addEmail}
                    className="mt-2 text-xs font-semibold text-pp-red hover:text-pp-red-light transition-colors flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Add email
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input value={contactFormData.phone} onChange={e => setContactFormData(f => ({ ...f, phone: e.target.value }))} className={inputCls} placeholder="Phone number" />
                  </div>
                  <div>
                    <label className={labelCls}>Type</label>
                    <select value={contactFormData.contact_type} onChange={e => setContactFormData(f => ({ ...f, contact_type: e.target.value }))} className={inputCls}>
                      <option value="owner">Owner</option>
                      <option value="leasing">Leasing</option>
                      <option value="broker">Broker</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Link to Property</label>
                    <select value={contactFormData.property_id} onChange={e => setContactFormData(f => ({ ...f, property_id: e.target.value }))} className={inputCls}>
                      <option value="">None</option>
                      {properties.map(p => (
                        <option key={p.id} value={p.id}>{p.address}{p.city ? `, ${p.city}` : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-pp-gray">
                <button onClick={() => setContactModal(null)} className="bg-[#ebebeb] hover:bg-[#d8d8d8] text-pp-dark text-sm font-medium px-4 py-2 rounded-lg transition-colors">Cancel</button>
                <button
                  onClick={handleSaveContactModal}
                  disabled={!canSave}
                  className="bg-pp-red hover:bg-pp-red-light disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors"
                  style={{ color: '#ffffff' }}
                >
                  {isEdit ? 'Save Changes' : 'Add Contact'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── LOI Preview / Edit Modal ── */}
      {loiModal && (() => {
        const { prop, contact, psf, subject, body, to, cc, confirmStep, cooldownAck } = loiModal;
        const cooldown = cooldownConflictFor(prop.id);
        const canProceed = !!to?.trim() && !!subject?.trim() && !!body?.trim() && !sendingLoi;
        const update = (patch) => setLoiModal(m => ({ ...m, ...patch }));
        const ccList = (cc || '').split(',').map(s => s.trim()).filter(Boolean);
        const inputBase = "w-full bg-[#242424] border border-[#3a3a3a] rounded-md px-3 py-2 text-sm text-white placeholder:text-[#8a8a8a] focus:outline-none focus:border-pp-red transition-colors";

        // ── Step 2: Confirmation summary ──
        if (confirmStep) {
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !sendingLoi && update({ confirmStep: false })}>
              <div className="bg-[#2c2c2c] rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
                <h3 className="text-base font-bold text-white mb-2">Send this LOI?</h3>
                <p className="text-xs text-[#8a8a8a] mb-4">This will email from your Outlook mailbox and create an Unsolicited Offer in the Pipeline.</p>
                <div className="bg-[rgba(36,36,36,0.6)] border border-[#3a3a3a] rounded-lg p-3 text-sm space-y-1.5 mb-5">
                  <div><span className="text-[#8a8a8a] text-xs uppercase tracking-wide">From</span><div className="text-white">acquisitions@proximityparks.com</div></div>
                  <div><span className="text-[#8a8a8a] text-xs uppercase tracking-wide">To</span><div className="text-[#f04f44]">{to}</div></div>
                  {ccList.length > 0 && <div><span className="text-[#8a8a8a] text-xs uppercase tracking-wide">CC</span><div className="text-[#b4b4b4]">{ccList.join(', ')}</div></div>}
                  <div><span className="text-[#8a8a8a] text-xs uppercase tracking-wide">Subject</span><div className="text-white">{subject}</div></div>
                  <div><span className="text-[#8a8a8a] text-xs uppercase tracking-wide">Offer</span><div className="text-white">${Number(psf).toLocaleString()}/SF on {Number(prop.rba || 0).toLocaleString()} SF</div></div>
                </div>
                <div className="flex justify-end gap-3">
                  <button disabled={sendingLoi} onClick={() => update({ confirmStep: false })} style={{ color: '#ffffff' }} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">Back</button>
                  <button disabled={!canProceed} onClick={handleSendLoi} style={{ color: '#ffffff' }} className="bg-pp-red hover:bg-pp-red-light disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2">
                    {sendingLoi ? (
                      <>
                        <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending…
                      </>
                    ) : 'Confirm & Send'}
                  </button>
                </div>
              </div>
            </div>
          );
        }

        // ── Step 1: Edit + preview ──
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => !sendingLoi && setLoiModal(null)}>
            <div className="bg-[#2c2c2c] rounded-xl p-6 w-full max-w-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Compose Letter of Intent</h3>
                <span className="text-xs text-[#8a8a8a]">${Number(psf).toLocaleString()}/SF · {Number(prop.rba || 0).toLocaleString()} SF</span>
              </div>

              {/* Phase 8 - the offer, derived rather than typed. Taking a
                  figure REWRITES the letter, so the number in the email is
                  always the number that was reasoned about; showing it without
                  regenerating the body would put one figure on screen and a
                  different one in the envelope. The derivation is kept on the
                  modal state so it can be logged with the outreach. */}
              <div className="mb-4">
                <OfferPricer
                  property={prop}
                  onUse={(newPsf, meta) => {
                    const regen = generateLoiBody(prop, contact, currentUser, newPsf, appSettings);
                    update({ psf: newPsf, body: regen.body, pricing: meta });
                  }}
                />
              </div>

              {/* Cooldown warning banner */}
              {cooldown && !cooldownAck && (
                <div className="mb-4 bg-[rgba(200,146,47,0.1)] border border-[rgba(200,146,47,0.3)] rounded-lg px-4 py-3 flex items-start gap-3">
                  <span className="text-[#c8922f] text-lg leading-none">⚠</span>
                  <div className="flex-1">
                    <div className="text-sm text-[#d6a955] font-semibold mb-0.5">Cooldown conflict</div>
                    <div className="text-xs text-[rgba(224,190,133,0.9)]">
                      An LOI was sent to this property <strong>{cooldown.days} day{cooldown.days === 1 ? '' : 's'} ago</strong> by {cooldown.prior.sent_by} — your cooldown is set to {cooldown.cooldownDays} days.
                    </div>
                  </div>
                  <button onClick={() => update({ cooldownAck: true })} className="text-xs text-[#d6a955] hover:text-[#e0be85] underline whitespace-nowrap">Send anyway</button>
                </div>
              )}

              {/* Sender (read-only) */}
              <div className="mb-3 bg-[rgba(58,58,58,0.5)] border border-[#3a3a3a] rounded-md px-3 py-2 text-sm">
                <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wide mb-0.5">Sending as {profileLoading && <span className="italic">(loading profile…)</span>}</div>
                <div className="text-white font-medium text-sm">{currentUser.name}{currentUser.jobTitle ? <span className="text-[#8a8a8a] font-normal text-xs"> — {currentUser.jobTitle}</span> : null}</div>
                <div className="text-[#8a8a8a] text-xs">
                  acquisitions@proximityparks.com
                  {currentUser.phone && <span> &middot; {currentUser.phone}</span>}
                </div>
              </div>

              {/* Editable recipient + CC + subject */}
              <div className="space-y-2 mb-3">
                <div>
                  <label className="text-[10px] text-[#8a8a8a] uppercase tracking-wide block mb-1">To</label>
                  <input type="email" value={to} onChange={e => update({ to: e.target.value })} placeholder="recipient@example.com" className={inputBase} />
                </div>
                <div>
                  <label className="text-[10px] text-[#8a8a8a] uppercase tracking-wide block mb-1">CC <span className="text-[#7a7a7a] normal-case">(comma-separated)</span></label>
                  <input type="text" value={cc} onChange={e => update({ cc: e.target.value })} placeholder="cc1@example.com, cc2@example.com" className={inputBase} />
                </div>
                <div>
                  <label className="text-[10px] text-[#8a8a8a] uppercase tracking-wide block mb-1">Subject</label>
                  <input type="text" value={subject} onChange={e => update({ subject: e.target.value })} className={inputBase} />
                </div>
              </div>

              {/* Editable body */}
              <div className="mb-4">
                <div className="flex items-baseline justify-between mb-1">
                  <label className="text-[10px] text-[#8a8a8a] uppercase tracking-wide">Body</label>
                  <button
                    onClick={() => {
                      const { body: fresh } = generateLoiBody(prop, contact, currentUser, psf, appSettings);
                      update({ body: fresh });
                    }}
                    className="text-[10px] text-[#8a8a8a] hover:text-[#f04f44] transition-colors"
                  >Reset to template</button>
                </div>
                <textarea
                  value={body}
                  onChange={e => update({ body: e.target.value })}
                  rows={16}
                  className={`${inputBase} font-mono text-xs leading-relaxed resize-y`}
                />
              </div>

              {/* Action buttons — inline color:#fff because the theme-light
                  override would otherwise turn text-white into dark text. */}
              <div className="flex flex-wrap justify-end gap-3">
                <button disabled={sendingLoi} onClick={() => setLoiModal(null)} style={{ color: '#ffffff' }} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">Cancel</button>
                <button disabled={sendingLoi} onClick={() => handleCopyLoi(body)} style={{ color: '#ffffff' }} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">Copy</button>
                <button disabled={sendingLoi} onClick={handleLogAsSent} style={{ color: '#ffffff' }} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors" title="Mark as sent without emailing from Proxy — use this if you sent manually from Outlook">Log as Sent</button>
                <button
                  disabled={!canProceed || (cooldown && !cooldownAck)}
                  onClick={() => update({ confirmStep: true })}
                  style={{ color: '#ffffff' }}
                  className="bg-pp-red hover:bg-pp-red-light disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                  title={cooldown && !cooldownAck ? 'Acknowledge cooldown warning to proceed' : `Send LOI from acquisitions@proximityparks.com`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="#ffffff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                  Review & Send
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Portfolio / Batch LOI Modal ── */}
      {portfolioLoiModal && (() => {
        const { groups } = portfolioLoiModal;
        const totalProps = groups.reduce((s, g) => s + g.props.length, 0);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setPortfolioLoiModal(null)}>
            <div className="bg-[#2c2c2c] rounded-xl p-6 w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-2">Batch LOI Preview</h3>
              <p className="text-sm text-[#8a8a8a] mb-4">
                {groups.length === 1
                  ? `${totalProps} properties to ${groups[0].owner}`
                  : `${totalProps} properties → ${groups.length} separate LOIs to ${groups.length} owners`}
              </p>

              <div className="mb-4 bg-[rgba(58,58,58,0.5)] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm">
                <div className="text-xs text-[#8a8a8a] mb-0.5">Sending as {profileLoading && <span className="italic">(loading profile…)</span>}</div>
                <div className="text-white font-medium">{currentUser.name}{currentUser.jobTitle ? <span className="text-[#8a8a8a] font-normal"> — {currentUser.jobTitle}</span> : null}</div>
                <div className="text-[#8a8a8a] text-xs">
                  acquisitions@proximityparks.com
                  {currentUser.phone && <span> &middot; {currentUser.phone}</span>}
                </div>
              </div>

              <div className="space-y-4">
                {groups.map((group, gi) => {
                  const isMulti = group.props.length > 1;
                  const genFn = isMulti ? generatePortfolioLoiBody : generateLoiBody;
                  const genArgs = isMulti
                    ? [group.props, group.contact, currentUser, group.psf, appSettings]
                    : [group.props[0], group.contact, currentUser, group.psf, appSettings];
                  const { body: defaultBody } = genFn(...genArgs);
                  const defaultSubject = isMulti
                    ? `Off-Market Portfolio Acquisition Interest — ${group.props.length} properties`
                    : `Off-Market Acquisition Interest — ${[group.props[0].address, group.props[0].city, group.props[0].state].filter(Boolean).join(', ')}`;
                  const defaultTo = group.contact?.email1 || '';
                  const defaultCc = [group.contact?.email2, group.contact?.email3, ...(Array.isArray(group.contact?.email_extras) ? group.contact.email_extras : [])].filter(Boolean).join(', ');

                  const edit = portfolioEdits[gi] || {};
                  const subject = edit.subject ?? defaultSubject;
                  const body    = edit.body    ?? defaultBody;
                  const to      = edit.to      ?? defaultTo;
                  const cc      = edit.cc      ?? defaultCc;
                  const setField = (field, value) => setPortfolioEdits(prev => ({ ...prev, [gi]: { ...prev[gi], [field]: value } }));
                  const resetField = (field) => setPortfolioEdits(prev => {
                    const next = { ...prev, [gi]: { ...prev[gi] } };
                    delete next[gi][field];
                    return next;
                  });

                  return (
                    <div key={gi} className="border border-[#3a3a3a] rounded-lg overflow-hidden">
                      <div className="bg-[rgba(58,58,58,0.5)] px-4 py-2 flex items-center justify-between flex-wrap gap-2">
                        <span className="text-sm text-white font-medium">{group.owner} — {group.props.length} propert{group.props.length === 1 ? 'y' : 'ies'}</span>
                        {(edit.subject != null || edit.body != null || edit.to != null || edit.cc != null) && (
                          <button
                            onClick={() => setPortfolioEdits(prev => { const next = { ...prev }; delete next[gi]; return next; })}
                            className="text-[10px] font-semibold text-[#f04f44] hover:text-[#f04f44]"
                            title="Discard edits for this group and restore the auto-generated text"
                          >Reset to default</button>
                        )}
                      </div>
                      <div className="bg-[#3a3a3a] p-3 space-y-2">
                        <div className="grid grid-cols-[60px_1fr] gap-2 items-center">
                          <label className="text-[10px] uppercase tracking-wider text-[#8a8a8a] text-right">To</label>
                          <input
                            value={to}
                            onChange={e => setField('to', e.target.value)}
                            onBlur={e => { if (e.target.value === defaultTo) resetField('to'); }}
                            className="bg-[#2c2c2c] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-pp-red"
                          />
                          <label className="text-[10px] uppercase tracking-wider text-[#8a8a8a] text-right">CC</label>
                          <input
                            value={cc}
                            onChange={e => setField('cc', e.target.value)}
                            onBlur={e => { if (e.target.value === defaultCc) resetField('cc'); }}
                            placeholder="Comma-separated"
                            className="bg-[#2c2c2c] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-pp-red"
                          />
                          <label className="text-[10px] uppercase tracking-wider text-[#8a8a8a] text-right">Subject</label>
                          <input
                            value={subject}
                            onChange={e => setField('subject', e.target.value)}
                            onBlur={e => { if (e.target.value === defaultSubject) resetField('subject'); }}
                            className="bg-[#2c2c2c] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-pp-red"
                          />
                        </div>
                        <textarea
                          value={body}
                          onChange={e => setField('body', e.target.value)}
                          onBlur={e => { if (e.target.value === defaultBody) resetField('body'); }}
                          rows={Math.min(16, Math.max(8, body.split('\n').length))}
                          className="w-full bg-[#2c2c2c] border border-[#4a4a4a] rounded p-3 font-mono text-xs text-white leading-relaxed focus:outline-none focus:border-pp-red resize-y"
                          spellCheck={true}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap justify-end gap-3 mt-6">
                <button disabled={sendingLoi} onClick={() => setPortfolioLoiModal(null)} style={{ color: '#ffffff' }} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">Cancel</button>
                <button disabled={sendingLoi} onClick={() => {
                  // Use the edited body when the user has changed it, otherwise the generated default.
                  const allBodies = groups.map((g, gi) => {
                    const edit = portfolioEdits[gi] || {};
                    if (edit.body != null) return edit.body;
                    const fn = g.props.length > 1 ? generatePortfolioLoiBody : generateLoiBody;
                    const args = g.props.length > 1
                      ? [g.props, g.contact, currentUser, g.psf, appSettings]
                      : [g.props[0], g.contact, currentUser, g.psf, appSettings];
                    return fn(...args).body;
                  }).join('\n\n---\n\n');
                  handleCopyLoi(allBodies);
                }} style={{ color: '#ffffff' }} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">Copy All</button>
                <button disabled={sendingLoi} onClick={handleLogPortfolioAsSent} style={{ color: '#ffffff' }} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">Log All as Sent</button>
                <button
                  disabled={sendingLoi}
                  onClick={handleSendPortfolioLois}
                  style={{ color: '#ffffff' }}
                  className="bg-pp-red hover:bg-pp-red-light disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                  title={`Send all LOIs from acquisitions@proximityparks.com`}
                >
                  {sendingLoi ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="#ffffff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                      Send All LOIs
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Toast ── */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm shadow-lg ${toast.type === 'info' ? 'bg-[rgba(138,138,138,0.22)] text-[#b4b4b4] border border-[rgba(138,138,138,0.35)]' : toast.type === 'success' ? 'bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)]' : 'bg-[rgba(233,53,41,0.2)] text-[#f4827a] border border-[rgba(233,53,41,0.3)]'}`}>
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-3 text-[#8a8a8a] hover:text-white">×</button>
        </div>
      )}

      {/* ── Create Deal Modal ── */}
      {createDealModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setCreateDealModal(null)}>
          <div className="bg-[#2c2c2c] rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4">Create Deal from Property</h3>
            <div className="space-y-3 mb-4">
              <p className="text-white font-medium">{createDealModal.address}</p>
              <p className="text-[#8a8a8a] text-sm">
                {createDealModal.rba ? `${Number(createDealModal.rba).toLocaleString()} SF` : ''}
                {createDealModal.owner_name ? ` · ${createDealModal.owner_name}` : ''}
              </p>
              <div>
                <label className="text-xs text-[#8a8a8a] mb-1 block">Pipeline Stage</label>
                <select value={createDealStage} onChange={e => setCreateDealStage(e.target.value)} className="w-full bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ring-[#e93529]">
                  {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#8a8a8a] mb-1 block">Market</label>
                <select value={createDealMarket || createDealModal.market || ''} onChange={e => setCreateDealMarket(e.target.value)} className="w-full bg-[#3a3a3a] border border-[#4a4a4a] text-[#b4b4b4] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ring-[#e93529]">
                  <option value="">Select market…</option>
                  {msaList.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCreateDealModal(null)} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleCreateDeal} disabled={createDealSaving} className="bg-[#44906a] hover:bg-[#4f9d76] disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                {createDealSaving ? 'Creating…' : 'Create Deal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Sourcing</h2>
          <p className="text-[#8a8a8a] text-sm">{properties.length} properties · {contacts.length} contacts</p>
        </div>
      </div>

      {/* ── Sub-tabs ── */}
      <div className="flex items-center gap-1 mb-6 border-b border-[#3a3a3a] pb-0">
        {[{ key: 'properties', label: 'LOI Sender' }, { key: 'contacts', label: 'Contacts' }, { key: 'outreach', label: 'Outreach' }].map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${subTab === t.key ? 'text-pp-red border-pp-red' : 'text-[#8a8a8a] border-transparent hover:text-[#b4b4b4]'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════ */}
      {/* PROPERTIES SUB-TAB                                                     */}
      {/* ════════════════════════════════════════════════════════════════════════ */}
      {subTab === 'properties' && (
        <>
          {/* ── Properties header row ── */}
          <div className="flex items-center justify-between mb-6">
            <input
              type="text"
              placeholder="Search address, city, owner…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#8a8a8a] w-64 focus:outline-none focus:border-[#e93529]"
            />
            <div className="flex items-center gap-3">
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 bg-pp-red hover:bg-pp-red-light disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                {uploading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Uploading…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    Upload CoStar
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  const missing = buckets.missing || [];
                  if (missing.length === 0) return;
                  const XLSX = require('xlsx');
                  const rows = missing.map(p => ({
                    'Address': p.address || '',
                    'City': p.city || '',
                    'State': p.state || '',
                    'Market': p.market || '',
                    'Owner Name': p.owner_name || '',
                    'SF': p.rba || '',
                    'Year Built': p.year_built || '',
                    'Clear Height': p.ceiling_ht || '',
                    'For Sale Status': p.for_sale_status || '',
                    'For Sale Price': p.for_sale_price || '',
                  }));
                  const ws = XLSX.utils.json_to_sheet(rows);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'Missing Contact');
                  XLSX.writeFile(wb, `Missing_Contact_Properties_${new Date().toISOString().slice(0,10)}.xlsx`);
                }}
                className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors hover:opacity-80"
                style={{ background: '#333333', color: '#ffffff' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                Export Missing Contact ({(buckets.missing || []).length})
              </button>
            </div>
          </div>

          {/* ── Upload status toast ── */}
          {uploadStatus && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${uploadStatus.type === 'success' ? 'bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)]' : 'bg-[rgba(233,53,41,0.2)] text-[#f4827a] border border-[rgba(233,53,41,0.3)]'}`}>
              {uploadStatus.message}
              <button onClick={() => setUploadStatus(null)} className="ml-3 text-[#8a8a8a] hover:text-white">×</button>
            </div>
          )}

          {/* ── Empty state ── */}
          {properties.length === 0 ? (
            <div className="text-center py-24 text-[#8a8a8a]">
              <p className="text-5xl mb-4">🏢</p>
              <p className="text-lg">No properties yet.</p>
              <p className="text-sm mt-2">Upload a CoStar export (.xlsx) to get started.</p>
            </div>
          ) : (
            <>
              {/* ── LOI pricing formula bar (Market NNN Rent + Year-3 YoC → base Total Price) ── */}
              {(() => {
                const selItems = buckets.ready.filter(p => selectedProps.has(p.id));
                const selTotalSF = selItems.reduce((s, p) => s + (p.rba || 0), 0);
                const ownerCount = new Set(selItems.map(p => p.owner_name || 'Unknown')).size;
                const hasSelection = selectedProps.size > 0;
                const totalPrice = getTotalPricePerSF();
                return (
                  <div className="mb-4 bg-[#242424] border border-[#3a3a3a] rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#f4f4f4' }}>LOI Pricing</span>
                      <span className="text-xs" style={{ color: '#8a8a8a' }}>· NNN ÷ YoC − CapEx</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold" style={{ color: '#f4f4f4' }}>Market NNN Rent</span>
                      <span className="text-xs" style={{ color: '#8a8a8a' }}>$</span>
                      <input
                        type="number" step="0.01" placeholder="—"
                        value={appSettings.market_nnn_rent || ''}
                        onChange={(e) => setAppSettings(prev => ({ ...prev, market_nnn_rent: e.target.value }))}
                        onBlur={async (e) => { await updateAppSetting('market_nnn_rent', e.target.value.trim()); }}
                        className="border border-[#c4c4c4] rounded px-2 py-1 text-xs w-20 focus:outline-none focus:border-pp-red"
                        style={{ color: '#f4f4f4', background: '#ffffff' }}
                      />
                      <span className="text-xs" style={{ color: '#8a8a8a' }}>/ SF</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold" style={{ color: '#f4f4f4' }}>Year-3 Trended YoC</span>
                      <input
                        type="number" step="0.01" placeholder="—"
                        value={appSettings.year3_trended_yoc || ''}
                        onChange={(e) => setAppSettings(prev => ({ ...prev, year3_trended_yoc: e.target.value }))}
                        onBlur={async (e) => { await updateAppSetting('year3_trended_yoc', e.target.value.trim()); }}
                        className="border border-[#c4c4c4] rounded px-2 py-1 text-xs w-20 focus:outline-none focus:border-pp-red"
                        style={{ color: '#f4f4f4', background: '#ffffff' }}
                      />
                      <span className="text-xs" style={{ color: '#8a8a8a' }}>%</span>
                    </div>
                    <div className="flex items-center gap-1.5 pl-2 border-l border-[#d8d8d8]">
                      <span className="text-xs" style={{ color: '#8a8a8a' }}>→ Base Total Price</span>
                      <span className="text-xs font-bold" style={{ color: totalPrice ? '#e93529' : '#c4c4c4' }}>
                        {totalPrice ? `$${totalPrice.toFixed(2)}` : '—'}
                      </span>
                      <span className="text-xs" style={{ color: '#8a8a8a' }}>/ SF</span>
                    </div>
                    {hasSelection && (
                      <>
                        <div className="w-px h-6 bg-[#d8d8d8] mx-1" />
                        <span className="text-xs font-medium" style={{ color: '#f4f4f4' }}>{selectedProps.size} selected · {Number(selTotalSF).toLocaleString()} SF</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs" style={{ color: '#8a8a8a' }}>$</span>
                          <input type="number" placeholder="Override" value={batchPsf} onChange={e => setBatchPsf(e.target.value)} className="border border-[#c4c4c4] rounded px-2 py-1 text-xs w-24 focus:outline-none focus:border-pp-red" style={{ color: '#f4f4f4', background: '#ffffff' }} />
                          <span className="text-xs" style={{ color: '#8a8a8a' }}>/ SF</span>
                        </div>
                        <button
                          onClick={() => openPortfolioLoi(selItems, selItems[0]?.contact, batchPsf)}
                          disabled={!canBatchLoi(selItems)}
                          className="text-xs bg-pp-red hover:bg-pp-red-light disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                        >
                          Preview Batch LOI{ownerCount > 1 ? ` (${ownerCount} owners)` : ''}
                        </button>
                        <button
                          onClick={handleDeleteSelectedProperties}
                          className="text-xs bg-[#c42d22] hover:bg-[#e93529] px-3 py-1.5 rounded-lg transition-colors font-medium flex items-center gap-1.5"
                          style={{ color: '#ffffff' }}
                          title="Delete selected properties"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="#ffffff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                          Delete
                        </button>
                        <button onClick={() => { setSelectedProps(new Set()); setBatchPsf(''); }} className="text-xs hover:underline transition-colors" style={{ color: '#8a8a8a' }}>Clear</button>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* ── Ready to Send (owner-grouped) ── */}
              <BucketSectionStable {...bucketDeps} id="ready" label="Ready to Send" color="border-[#4f9d76]" items={buckets.ready}>
                {(items) => {
                  // Group by owner
                  const ownerMap = {};
                  for (const p of items) {
                    const owner = p.owner_name || 'Unknown Owner';
                    if (!ownerMap[owner]) ownerMap[owner] = [];
                    ownerMap[owner].push(p);
                  }
                  const ownerGroups = Object.entries(ownerMap).sort((a, b) => b[1].length - a[1].length);

                  // Select All checkbox
                  const allIds = items.map(p => p.id);
                  const allSelected = allIds.length > 0 && allIds.every(id => selectedProps.has(id));

                  return (
                    <>
                      {/* Select All */}
                      <div className="flex items-center gap-2 px-4 pb-2">
                        <label className="flex items-center gap-2 cursor-pointer text-xs text-[#8a8a8a] hover:text-[#b4b4b4]">
                          <input type="checkbox" checked={allSelected} onChange={() => toggleSelectAll(items)} className="accent-pp-red w-3.5 h-3.5" />
                          Select All ({items.length})
                        </label>
                      </div>
                      {ownerGroups.map(([owner, ownerProps]) => {
                        const totalSF = ownerProps.reduce((s, p) => s + (p.rba || 0), 0);
                        const contact = ownerProps[0]?.contact;
                        const isMulti = ownerProps.length > 1;
                        const isExpanded = expandedOwners[owner] !== false; // default expanded
                        const ownerPsf = ownerPsfValues[owner] || '';

                        if (!isMulti) {
                          // Single property — render inline as before
                          const p = ownerProps[0];
                          return (
                            <div key={p.id} className={`rounded-lg p-4 mb-2 transition-colors ${selectedProps.has(p.id) ? 'ring-2 ring-[rgba(233,53,41,0.5)]' : ''}`} style={p.classification ? { background: classRowBg[p.classification], border: `1px solid ${classRowBorder[p.classification]}` } : { background: '#ffffff', border: '1px solid #e4e4e4' }}>
                              <div className="grid gap-4 items-center" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 2fr 1fr 1fr 1fr 1fr 1fr" }}>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <input type="checkbox" checked={selectedProps.has(p.id)} onChange={() => togglePropSelection(p.id)} className="accent-pp-red w-3.5 h-3.5 flex-shrink-0" onClick={e => e.stopPropagation()} />
                                    <div className="min-w-0">
                                      <p className="font-semibold text-white truncate">{p.address}</p>
                                      <p className="text-[#8a8a8a] text-xs">{[p.city, p.state].filter(Boolean).join(', ')}</p>
                                    </div>
                                  </div>
                                </div>
                                <div onClick={e => e.stopPropagation()}>
                                  <input
                                    type="number" step="0.01" placeholder="—"
                                    defaultValue={p.capex_per_sf ?? ''}
                                    onBlur={async (e) => {
                                      const raw = e.target.value.trim();
                                      const v = raw === '' ? null : parseFloat(raw);
                                      if (v !== null && isNaN(v)) return;
                                      if (v === (p.capex_per_sf ?? null)) return;
                                      try { await updateProperty(p.id, { capex_per_sf: v }); await reloadAll(); } catch {}
                                    }}
                                    className="bg-[#3a3a3a] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-pp-red w-full"
                                    title="CapEx per SF — subtracted from the base Total Price for this property's offer"
                                  />
                                </div>
                                <div className="text-sm text-[#b4b4b4]">{p.rba ? `${fmtNum(p.rba)} SF` : '—'}</div>
                                <div className="text-sm text-[#b4b4b4]">{p.year_built || '—'}</div>
                                <div className="text-sm text-[#b4b4b4] truncate">{p.owner_name || '—'}</div>
                                <div className="text-sm text-[#b4b4b4]">{p.ceiling_ht || '—'}</div>
                                <div className="text-sm text-[#b4b4b4]">{p.loading_docks != null ? p.loading_docks : '—'}</div>
                                <div className="text-sm text-[#b4b4b4] truncate">{p.for_sale_status || '—'}</div>
                                <div className="text-sm text-[#b4b4b4]">{fmtPct(p.percent_leased)}</div>
                                <div />
                              </div>
                              <div className="mt-2 pt-2 border-t border-[#3a3a3a] flex items-center gap-4 text-sm">
                                <span className="text-[#4f9d76]">✉ {contact?.name || 'Contact'}</span>
                                <span className="text-[#8a8a8a]">{contact?.email1}</span>
                                <div className="ml-auto flex items-center gap-2">
                                  <span className="text-[#8a8a8a] text-xs">$</span>
                                  <input type="number" placeholder={getPsfForProp(p) ? `${getPsfForProp(p)} (formula)` : "PSF"} value={psfValues[p.id] || ''} onChange={e => setPsfValues(prev => ({ ...prev, [p.id]: e.target.value }))} className="bg-[#3a3a3a] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white w-32 focus:outline-none focus:border-[#e93529]" />
                                  <span className="text-[#8a8a8a] text-xs">/ SF</span>
                                  <button onClick={() => openLoiPreview(p)} disabled={!getPsfForProp(p) || isNaN(parseFloat(getPsfForProp(p)))} className="text-xs bg-pp-red hover:bg-pp-red-light disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1 rounded transition-colors">Preview LOI</button>
                                  <button onClick={() => handleDeleteProperty(p)} className="text-[#8a8a8a] hover:text-[#f04f44] transition-colors p-1 rounded" title="Delete property">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Multi-property owner group
                        return (
                          <div key={owner} className="mb-3">
                            {/* Owner group header */}
                            <button
                              onClick={() => setExpandedOwners(prev => ({ ...prev, [owner]: !isExpanded }))}
                              className="w-full bg-[rgba(58,58,58,0.5)] rounded-lg p-3 mb-1 flex items-center gap-3 hover:bg-[#3a3a3a] transition-colors text-left"
                            >
                              <svg className={`w-3.5 h-3.5 text-[#8a8a8a] transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                              <span className="text-white font-semibold text-sm">{owner}</span>
                              <span className="text-[#8a8a8a] text-xs">{ownerProps.length} properties · {Number(totalSF).toLocaleString()} SF total</span>
                              <label className="ml-auto flex items-center gap-1.5 text-xs text-[#8a8a8a]" onClick={e => e.stopPropagation()}>
                                <input type="checkbox" checked={ownerProps.every(p => selectedProps.has(p.id))} onChange={() => toggleSelectAll(ownerProps)} className="accent-pp-red w-3.5 h-3.5" />
                              </label>
                            </button>

                            {isExpanded && (
                              <>
                                {/* Property rows */}
                                {ownerProps.map(p => (
                                  <div key={p.id} className={`rounded-lg p-4 mb-1 transition-colors ${selectedProps.has(p.id) ? 'ring-2 ring-[rgba(233,53,41,0.5)]' : ''}`} style={p.classification ? { background: classRowBg[p.classification], border: `1px solid ${classRowBorder[p.classification]}` } : { background: '#ffffff', border: '1px solid #e4e4e4' }}>
                                    <div className="grid gap-4 items-center" style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 2fr 1fr 1fr 1fr 1fr 1fr" }}>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <input type="checkbox" checked={selectedProps.has(p.id)} onChange={() => togglePropSelection(p.id)} className="accent-pp-red w-3.5 h-3.5 flex-shrink-0" />
                                          <div className="min-w-0">
                                            <p className="text-sm text-white truncate">{p.address}</p>
                                            <p className="text-xs text-[#8a8a8a]">{[p.city, p.state].filter(Boolean).join(', ')}</p>
                                          </div>
                                        </div>
                                      </div>
                                      <div onClick={e => e.stopPropagation()}>
                                        <input
                                          type="number" step="0.01" placeholder="—"
                                          defaultValue={p.capex_per_sf ?? ''}
                                          onBlur={async (e) => {
                                            const raw = e.target.value.trim();
                                            const v = raw === '' ? null : parseFloat(raw);
                                            if (v !== null && isNaN(v)) return;
                                            if (v === (p.capex_per_sf ?? null)) return;
                                            try { await updateProperty(p.id, { capex_per_sf: v }); await reloadAll(); } catch {}
                                          }}
                                          className="bg-[#3a3a3a] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-pp-red w-full"
                                          title="CapEx per SF — subtracted from base Total Price"
                                        />
                                      </div>
                                      <div className="text-xs text-[#b4b4b4]">{p.rba ? fmtNum(p.rba) : '—'}</div>
                                      <div className="text-xs text-[#8a8a8a]">{p.year_built || '—'}</div>
                                      <div className="text-xs text-[#8a8a8a] truncate">{p.owner_name || '—'}</div>
                                      <div className="text-xs text-[#8a8a8a]">{p.ceiling_ht || '—'}</div>
                                      <div className="text-xs text-[#8a8a8a]">{p.loading_docks ?? '—'}</div>
                                      <div className="text-xs text-[#8a8a8a]">{p.for_sale_status || '—'}</div>
                                      <div className="text-xs text-[#8a8a8a]">{p.percent_leased != null ? p.percent_leased + '%' : '—'}</div>
                                      <div className="flex justify-end">
                                        <button onClick={() => handleDeleteProperty(p)} className="text-[#8a8a8a] hover:text-[#f04f44] transition-colors p-1 rounded" title="Delete property">
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {/* Contact + Portfolio LOI controls */}
                                <div className="ml-4 mt-2 flex items-center gap-4 text-sm flex-wrap">
                                  <span className="text-[#4f9d76]">✉ {contact?.name || 'Contact'}</span>
                                  <span className="text-[#8a8a8a]">{contact?.email1}</span>
                                  <div className="ml-auto flex items-center gap-2">
                                    <span className="text-[#8a8a8a] text-xs">$</span>
                                    <input type="number" placeholder="Class default" value={ownerPsf} onChange={e => setOwnerPsfValues(prev => ({ ...prev, [owner]: e.target.value }))} className="bg-[#3a3a3a] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white w-32 focus:outline-none focus:border-[#e93529]" />
                                    <span className="text-[#8a8a8a] text-xs">/ SF</span>
                                    <button
                                      onClick={() => openPortfolioLoi(ownerProps, contact, ownerPsf)}
                                      disabled={!canBatchLoi(ownerProps) && !(ownerPsf && !isNaN(parseFloat(ownerPsf)))}
                                      className="text-xs bg-pp-red hover:bg-pp-red-light disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1 rounded transition-colors"
                                    >
                                      Preview Portfolio LOI
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                }}
              </BucketSectionStable>

              {/* ── Missing Contact ── */}
              <BucketSectionStable {...bucketDeps} id="missing" label="Missing Contact" color="border-[#c8922f]" items={buckets.missing}>
                {(items) => items.map(p => (
                  <PropertyRow key={p.id} prop={p} bucket="missing" extra={
                    <div className="mt-2 pt-2 border-t border-[#3a3a3a]">
                      <button
                        onClick={() => openAddContactModal({ owner_name: p.owner_name || '', property_id: p.id, contact_type: 'owner' })}
                        className="text-[#c8922f] hover:text-[#d6a955] text-xs underline"
                      >
                        + Add Contact
                      </button>
                    </div>
                  } />
                ))}
              </BucketSectionStable>

              {/* ── Contacted Recently ── */}
              <BucketSectionStable {...bucketDeps} id="recent" label="Contacted Recently" color="border-[#8a8a8a]" items={buckets.recent}>
                {(items) => items.map(p => (
                  <PropertyRow key={p.id} prop={p} bucket="recent" extra={
                    <div className="mt-2 pt-2 border-t border-[#3a3a3a] flex items-center gap-4 text-xs text-[#8a8a8a]">
                      <span>Last contacted <strong className="text-[#b4b4b4]">{p.days_since} days ago</strong></span>
                      <span>Eligible again on <strong className="text-[#b4b4b4]">{new Date(p.ready_on).toLocaleDateString()}</strong></span>
                      {p.sent_by && <span>Sent by {p.sent_by}</span>}
                    </div>
                  } />
                ))}
              </BucketSectionStable>

              {/* ── Institutional ── */}
              <BucketSectionStable {...bucketDeps} id="institutional" label="Institutional" color="border-[#5a5a5a]" items={buckets.institutional}>
                {(items) => {
                  const grouped = {};
                  for (const p of items) {
                    const key = p.owner_name || 'Unknown';
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(p);
                  }
                  return Object.entries(grouped).sort((a, b) => b[1].length - a[1].length).map(([owner, props]) => (
                    <div key={owner} className="mb-4">
                      <div className="flex items-center gap-2 px-4 py-2 mb-1">
                        <span className="text-[#b4b4b4] font-medium text-sm">{owner}</span>
                        <span className="bg-[#3a3a3a] text-[#8a8a8a] text-xs px-2 py-0.5 rounded-full">{props.length}</span>
                      </div>
                      {props.map(p => <PropertyRow key={p.id} prop={p} bucket="institutional" />)}
                    </div>
                  ));
                }}
              </BucketSectionStable>
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════════ */}
      {/* CONTACTS SUB-TAB                                                       */}
      {/* ════════════════════════════════════════════════════════════════════════ */}
      {subTab === 'contacts' && (
        <>
          {/* ── Contacts header ── */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search name, email, owner, phone…"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#8a8a8a] w-64 focus:outline-none focus:border-[#e93529]"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => contactFileInputRef.current?.click()}
                disabled={contactUploading}
                className="flex items-center gap-2 bg-pp-red hover:bg-pp-red-light disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors" style={{ color: '#ffffff' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                {contactUploading ? "Uploading..." : "Upload Contacts"}
              </button>
              <input ref={contactFileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => e.target.files?.[0] && handleContactFileUpload(e.target.files[0])} />
              <button
                onClick={() => openAddContactModal()}
                className="flex items-center gap-2 bg-[#4a4a4a] hover:bg-[#6a6a6a] text-sm font-medium px-4 py-2 rounded-lg transition-colors" style={{ color: '#ffffff' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Contact
              </button>
            </div>
          </div>

          {/* Upload status banner */}
          {contactUploadStatus && (
            <div className="mb-4 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-4 py-3 text-sm text-[#b4b4b4] flex items-center gap-2">
              {contactUploading && <span className="animate-pulse text-pp-red">●</span>}
              {contactUploadStatus}
            </div>
          )}

          {/* ── Email verification legend (clickable filters) ── */}
          <div className="mb-4 bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.4)] rounded-lg px-4 py-3">
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs">
              <span className="text-[#8a8a8a] font-semibold uppercase tracking-wider mr-1">Email Status:</span>
              {showTrashedEmails ? (
                /* Trashed view statuses */
                <>
                  {[
                    { key: "invalid", icon: "\u274C", label: "Invalid", count: trashedStatusCounts.invalid, cls: "text-[#f04f44]" },
                    { key: "spamtrap", icon: "\uD83D\uDEAB", label: "Spam Trap", count: trashedStatusCounts.spamtrap, cls: "text-[#e93529]" },
                    { key: "abuse", icon: "\uD83D\uDEAB", label: "Abuse", count: trashedStatusCounts.abuse, cls: "text-[#e93529]" },
                    { key: "do_not_mail", icon: "\uD83D\uDEAB", label: "Do Not Mail", count: trashedStatusCounts.do_not_mail, cls: "text-[#f04f44]" },
                  ].map(s => (
                    <button key={s.key} onClick={() => setEmailStatusFilter(f => f === s.key ? null : s.key)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors cursor-pointer ${emailStatusFilter === s.key ? 'bg-[#4a4a4a] ring-1 ring-[#8a8a8a]' : 'hover:bg-[rgba(58,58,58,0.5)]'}`}
                      style={emailStatusFilter === s.key ? { color: '#ffffff' } : undefined}>
                      <span className={s.cls}>{s.icon}</span> {s.label} ({s.count.toLocaleString()})
                    </button>
                  ))}
                  {emailStatusFilter && (
                    <button onClick={() => setEmailStatusFilter(null)} className="text-[#f04f44] hover:text-[#f04f44] px-2 py-0.5 rounded-full bg-[#3a3a3a] hover:bg-[#4a4a4a] transition-colors ml-1">
                      Clear Filter
                    </button>
                  )}
                </>
              ) : (
                /* Normal contacts view statuses */
                <>
                  {[
                    { key: "valid", icon: "\u2705", label: "Valid", count: emailStatusCounts.valid, cls: "text-[#4f9d76]" },
                    { key: "catch-all", icon: "\u26A0\uFE0F", label: "Catch-All", count: emailStatusCounts["catch-all"], cls: "text-[#c8922f]" },
                    { key: "unknown", icon: "\u2754", label: "Unknown", count: emailStatusCounts.unknown, cls: "text-[#8a8a8a]" },
                    { key: "disposable", icon: "\uD83D\uDDD1\uFE0F", label: "Disposable", count: emailStatusCounts.disposable, cls: "text-[#c8922f]" },
                    { key: "unverified", icon: "—", label: "Not Verified", count: emailStatusCounts.unverified, cls: "text-[#7a7a7a]" },
                    { key: "missing_contact", icon: "\uD83D\uDC64", label: "Missing Contact", count: emailStatusCounts.missing_contact, cls: "text-[#8a8a8a]" },
                    { key: "needs_update", icon: "\u270F\uFE0F", label: "Needs Update", count: emailStatusCounts.needs_update, cls: "text-pp-red" },
                  ].map(s => (
                    <button key={s.key} onClick={() => setEmailStatusFilter(f => f === s.key ? null : s.key)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors cursor-pointer ${emailStatusFilter === s.key ? 'bg-[#4a4a4a] ring-1 ring-[#8a8a8a]' : 'hover:bg-[rgba(58,58,58,0.5)]'}`}
                      style={emailStatusFilter === s.key ? { color: '#ffffff' } : undefined}>
                      <span className={s.cls}>{s.icon}</span> {s.label} ({s.count.toLocaleString()})
                    </button>
                  ))}
                  {emailStatusFilter && (
                    <button onClick={() => setEmailStatusFilter(null)} className="text-[#f04f44] hover:text-[#f04f44] px-2 py-0.5 rounded-full bg-[#3a3a3a] hover:bg-[#4a4a4a] transition-colors ml-1">
                      Clear Filter
                    </button>
                  )}
                </>
              )}
              <span className="ml-auto">
                <button onClick={() => { setShowTrashedEmails(v => !v); setEmailStatusFilter(null); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${showTrashedEmails ? 'bg-[#c42d22] text-white' : 'bg-[#3a3a3a] text-[#f04f44] hover:bg-[#4a4a4a]'}`}>
                  {"\uD83D\uDDD1\uFE0F"} Trashed Emails ({emailStatusCounts.trashed.toLocaleString()})
                </button>
              </span>
            </div>
          </div>

          {/* ── Trashed Emails view ── */}
          {showTrashedEmails ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#f04f44]">Trashed Emails ({filteredTrashed.length}{emailStatusFilter ? ` of ${trashedEmails.length}` : ""})</h3>
                <button onClick={() => setShowTrashedEmails(false)} className="text-xs text-[#8a8a8a] hover:text-white px-3 py-1.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded-lg transition-colors">
                  Back to Contacts
                </button>
              </div>
              <p className="text-xs text-[#8a8a8a] mb-4">These emails were flagged as Invalid, Spam Trap, Abuse, or Do Not Mail by ZeroBounce and have been removed from contact cards.</p>
              {filteredTrashed.length === 0 ? (
                <div className="text-center py-16 text-[#8a8a8a]">{emailStatusFilter ? "No emails match this filter." : "No trashed emails."}</div>
              ) : (
                <div className="space-y-1">
                  {filteredTrashed.map((t, i) => {
                    const statusLabel = { invalid: "Invalid", spamtrap: "Spam Trap", abuse: "Abuse", do_not_mail: "Do Not Mail" }[t.status] || t.status;
                    return (
                      <div key={i} className="bg-[#2c2c2c] rounded-lg px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4 min-w-0">
                          <span className="text-[#f04f44] text-sm">{"\u274C"}</span>
                          <span className="text-[#b4b4b4] text-sm font-mono truncate">{t.email}</span>
                          <span className="text-xs bg-[rgba(196,45,34,0.3)] text-[#f04f44] border border-[rgba(196,45,34,0.3)] px-2 py-0.5 rounded-full">{statusLabel}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#8a8a8a] flex-shrink-0">
                          <span>{t.contact.name || "Unknown"}</span>
                          <span>{t.contact.owner_name || ""}</span>
                          <span className="text-[#7a7a7a]">({t.field})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
          <>
          {/* ── Contacts list ── */}
          {filteredContacts.length === 0 ? (
            <div className="text-center py-24 text-[#8a8a8a]">
              <p className="text-5xl mb-4">👤</p>
              <p className="text-lg">{contacts.length === 0 ? 'No contacts yet.' : 'No contacts match your search.'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map(c => {
                const isExpanded = expandedContactId === c.id;
                const ownerProps = c.owner_name ? properties.filter(p => p.owner_name && p.owner_name.toLowerCase().trim() === c.owner_name.toLowerCase().trim()) : [];
                const visibleEmails = [
                  c.email1 && !TRASHED_STATUSES.has(c.email1_status) ? c.email1 : null,
                  c.email2 && !TRASHED_STATUSES.has(c.email2_status) ? c.email2 : null,
                  c.email3 && !TRASHED_STATUSES.has(c.email3_status) ? c.email3 : null,
                ].filter(Boolean);
                const hasValidEmail = [c.email1_status, c.email2_status, c.email3_status].some(s => s === "valid");
                const missingContact = visibleEmails.length === 0;
                const needsUpdate = visibleEmails.length > 0 && !hasValidEmail;
                return (
                  <div key={c.id} className={`rounded-lg overflow-hidden transition-colors ${(needsUpdate || missingContact) ? '' : 'bg-[#2c2c2c]'}`} style={needsUpdate ? { background: 'rgba(233,53,41,0.12)', border: '1px solid rgba(233,53,41,0.25)' } : missingContact ? { background: 'rgba(138,138,138,0.15)', border: '1px solid rgba(138,138,138,0.3)' } : undefined}>
                    <div className="p-4 hover:bg-[rgba(58,58,58,0.5)] cursor-pointer" onClick={() => setExpandedContactId(isExpanded ? null : c.id)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {/* Company name (top line) */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[#8a8a8a] text-xs transition-transform" style={{ display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'none' }}>▶</span>
                            <span className="text-white font-semibold">{c.owner_name || c.name}</span>
                            {missingContact && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#333333', color: '#ffffff' }}>Missing Contact</span>}
                            {needsUpdate && <span className="text-xs px-2 py-0.5 rounded-full bg-[#c42d22]" style={{ color: '#ffffff' }}>Update Email</span>}
                            {ownerProps.length > 0 && <span className="text-xs bg-[#3a3a3a] text-[#8a8a8a] rounded px-1.5 py-0.5">{ownerProps.length} propert{ownerProps.length === 1 ? 'y' : 'ies'}</span>}
                          </div>
                          {/* Primary contact */}
                          <div className="flex items-center gap-2 ml-5 mb-1">
                            <span className="text-[#b4b4b4] text-sm">{c.name}</span>
                            {c.title && <span className="text-[#8a8a8a] text-xs">· {c.title}</span>}
                          </div>
                          {/* Emails — primary (email1) then every other email
                              including email_extras (4+). Each non-primary
                              gets a "CC:" tag before it. */}
                          <div className="flex items-center gap-4 mt-1 text-sm flex-wrap ml-5">
                            <EmailWithStatus email={c.email1} status={c.email1_status} />
                            {[
                              { email: c.email2, status: c.email2_status },
                              { email: c.email3, status: c.email3_status },
                              ...(Array.isArray(c.email_extras) ? c.email_extras : []).map(e => ({ email: e, status: null })),
                            ]
                              .filter(x => x.email && !TRASHED_STATUSES.has(x.status))
                              .map((x, i) => (
                                <span key={`${x.email}-${i}`} className="flex items-center gap-1.5">
                                  <span className="text-[#7a7a7a] text-xs">CC:</span>
                                  <EmailWithStatus email={x.email} status={x.status} />
                                </span>
                              ))}
                            {c.phone && <span className="text-[#b4b4b4]">{c.phone}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4 shrink-0" onClick={e => e.stopPropagation()}>
                          <button onClick={() => openEditContactModal(c)} className="bg-[#4a4a4a] hover:bg-[#6a6a6a] text-xs px-3 py-1.5 rounded-lg transition-colors" style={{ color: '#ffffff' }}>Edit</button>
                          <button onClick={() => handleDeleteContact(c)} className="bg-[#c42d22] hover:bg-[#e93529] text-xs px-3 py-1.5 rounded-lg transition-colors" style={{ color: '#ffffff' }}>Delete</button>
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-[rgba(58,58,58,0.5)] bg-[rgba(44,44,44,0.5)] px-4 py-3">
                        {ownerProps.length === 0 ? (
                          <p className="text-xs text-[#8a8a8a] py-2">No properties found for this owner.</p>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-xs text-[#8a8a8a] font-semibold uppercase tracking-wider mb-2">Properties owned by {c.owner_name} ({ownerProps.length})</p>
                            {ownerProps.map(p => (
                              <div key={p.id} className="bg-[rgba(58,58,58,0.4)] rounded-lg px-3 py-2 flex items-center justify-between hover:bg-[rgba(58,58,58,0.6)] transition-colors">
                                <div className="min-w-0">
                                  <p className="text-sm text-white font-medium truncate">{p.address}{p.city ? `, ${p.city}` : ''}{p.state ? `, ${p.state}` : ''}</p>
                                  <div className="flex items-center gap-3 text-xs text-[#8a8a8a] mt-0.5">
                                    {p.rba ? <span>{Number(p.rba).toLocaleString()} SF</span> : null}
                                    {p.year_built ? <span>Built {p.year_built}</span> : null}
                                    {p.for_sale_price ? <span className="text-[#f04f44]">${Number(p.for_sale_price).toLocaleString()}</span> : null}
                                    {p.market ? <span>{p.market}</span> : null}
                                  </div>
                                </div>
                                <button onClick={() => { setSubTab('properties'); setSearchTerm(p.address || ''); }}
                                  className="text-xs text-[#f04f44] hover:text-[#f04f44] shrink-0 ml-3">View →</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════════ */}
      {/* OUTREACH SUB-TAB                                                       */}
      {/* ════════════════════════════════════════════════════════════════════════ */}
      {subTab === 'outreach' && (() => {
        const cooldownDays = parseInt(appSettings.cooldown_days || '14', 10);
        const now = Date.now();
        const senderNames = [...new Set(outreachLog.map(e => e.sent_by).filter(Boolean))];

        // Compute days since sent for each entry
        const daysSinceSent = (entry) => entry.sent_at ? Math.floor((now - new Date(entry.sent_at)) / 86400000) : 0;

        // Filter
        let filtered = outreachLog.filter(entry => {
          if (outreachFollowUpOnly && !(entry.status === 'Sent' && daysSinceSent(entry) >= cooldownDays)) return false;
          if (outreachStatusFilter !== 'All' && entry.status !== outreachStatusFilter) return false;
          if (outreachSentByFilter !== 'All' && entry.sent_by !== outreachSentByFilter) return false;
          if (outreachSearch.trim()) {
            const term = outreachSearch.toLowerCase();
            const prop = properties.find(p => p.id === entry.property_id);
            const addr = prop ? (prop.address || '').toLowerCase() : '';
            const contactName = contacts.find(c => c.id === entry.contact_id)?.name?.toLowerCase() || '';
            if (!addr.includes(term) && !contactName.includes(term)) return false;
          }
          return true;
        });

        // Sort
        if (outreachSort === 'newest') filtered.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
        else if (outreachSort === 'oldest') filtered.sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
        else if (outreachSort === 'owner') filtered.sort((a, b) => {
          const pa = properties.find(p => p.id === a.property_id);
          const pb = properties.find(p => p.id === b.property_id);
          return ((pa?.owner_name || '') > (pb?.owner_name || '') ? 1 : -1);
        });
        else if (outreachSort === 'status') filtered.sort((a, b) => (a.status || '').localeCompare(b.status || ''));

        // Stats
        const totalSent = outreachLog.length;
        const awaiting = outreachLog.filter(e => e.status === 'Sent').length;
        const responded = outreachLog.filter(e => e.status === 'Responded').length;
        const counter = outreachLog.filter(e => e.status === 'Counter').length;
        const passed = outreachLog.filter(e => e.status === 'Passed').length;
        // Response rate = anything that got a real reply (Responded + Counter + Passed)
        // divided by total sent. "Sent" means we never heard back.
        const replied = responded + counter + passed;
        const responseRate = totalSent > 0 ? ((replied / totalSent) * 100).toFixed(1) : '0';
        const followUpNeeded = outreachLog.filter(e => e.status === 'Sent' && daysSinceSent(e) >= cooldownDays).length;

        // Avg days to reply — only for entries with a known sent_at and a reply.
        // We don't store reply timestamps, so this is best-effort: assume status
        // changed today for entries currently in a replied state without a
        // dedicated reply_at column. Future improvement: persist replied_at.
        const repliedEntries = outreachLog.filter(e => ['Responded', 'Counter', 'Passed'].includes(e.status) && e.sent_at);
        const avgDaysToReply = repliedEntries.length
          ? Math.round(repliedEntries.reduce((sum, e) => sum + daysSinceSent(e), 0) / repliedEntries.length)
          : null;

        // Response rate by market — only meaningful if we have ≥3 LOIs in a market.
        const marketStats = {};
        outreachLog.forEach(e => {
          const prop = properties.find(p => p.id === e.property_id);
          const m = prop?.market || 'Unknown';
          if (!marketStats[m]) marketStats[m] = { sent: 0, replied: 0 };
          marketStats[m].sent++;
          if (['Responded', 'Counter', 'Passed'].includes(e.status)) marketStats[m].replied++;
        });
        const marketRanked = Object.entries(marketStats)
          .filter(([, s]) => s.sent >= 3)
          .map(([m, s]) => ({ market: m, sent: s.sent, replied: s.replied, rate: (s.replied / s.sent) * 100 }))
          .sort((a, b) => b.rate - a.rate);

        // Sender leaderboard
        const senderStats = {};
        outreachLog.forEach(e => {
          const s = e.sent_by || 'Unknown';
          if (!senderStats[s]) senderStats[s] = { sent: 0, replied: 0 };
          senderStats[s].sent++;
          if (['Responded', 'Counter', 'Passed'].includes(e.status)) senderStats[s].replied++;
        });
        const senderRanked = Object.entries(senderStats)
          .map(([s, v]) => ({ sender: s, sent: v.sent, replied: v.replied, rate: v.sent ? (v.replied / v.sent) * 100 : 0 }))
          .sort((a, b) => b.sent - a.sent);

        const statusBadge = (status) => {
          const colors = { Sent: 'bg-[#c42d22]', Responded: 'bg-[#44906a]', Counter: 'bg-[#4a4a4a]', Passed: 'bg-[#c42d22]', Pending: 'bg-[#4a4a4a]' };
          return <span className={`${colors[status] || 'bg-[#4a4a4a]'} text-white text-xs px-2 py-0.5 rounded-full`}>{status}</span>;
        };

        // CSV export
        const exportCSV = () => {
          const headers = ['Property Address','City','State','Owner Name','Contact Name','Contact Email','Sent By','Sent Date','Offer PSF','Total Amount','Deposit','Expiration','Status','Response','Days Since Sent'];
          const rows = filtered.map(e => {
            const prop = properties.find(p => p.id === e.property_id) || {};
            const contact = contacts.find(c => c.id === e.contact_id) || {};
            return [
              prop.address || '', prop.city || '', prop.state || '', prop.owner_name || '',
              contact.name || '', contact.email1 || '', e.sent_by || '',
              e.sent_at ? new Date(e.sent_at).toLocaleDateString() : '',
              e.loi_psf || '', e.loi_total || '', e.deposit_amount || '',
              e.expiration_date || '', e.status || '', e.response || '', daysSinceSent(e)
            ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
          });
          const csv = [headers.join(','), ...rows].join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `outreach-export-${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        };

        return (
          <>
            {/* ── Stats bar ── */}
            <div className="grid grid-cols-6 gap-3 mb-4">
              {[
                { label: 'Total Sent', value: totalSent, color: 'text-white' },
                { label: 'Awaiting', value: awaiting, color: 'text-[#f04f44]' },
                { label: 'Responded', value: responded, color: 'text-[#4f9d76]' },
                { label: 'Counter', value: counter, color: 'text-[#b4b4b4]' },
                { label: 'Response Rate', value: `${responseRate}%`, color: 'text-pp-red' },
                { label: 'Follow Up', value: followUpNeeded, color: 'text-[#f04f44]' },
              ].map(s => (
                <div key={s.label} className="bg-[#2c2c2c] rounded-lg p-3">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-[#8a8a8a]">{s.label}</p>
                </div>
              ))}
            </div>

            {/* ── Analytics: market + sender breakdowns ── */}
            {totalSent > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-[#2c2c2c] rounded-lg p-4">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-xs font-bold text-[#b4b4b4] uppercase tracking-wider">Response Rate by Market</h3>
                    <span className="text-[10px] text-[#8a8a8a]">≥3 LOIs</span>
                  </div>
                  {marketRanked.length === 0 ? (
                    <p className="text-xs text-[#8a8a8a] py-2">Not enough data yet — need 3+ LOIs in a market.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {marketRanked.slice(0, 6).map(m => (
                        <div key={m.market}>
                          <div className="flex items-baseline justify-between text-xs mb-0.5">
                            <span className="text-[#b4b4b4] truncate">{m.market}</span>
                            <span className="text-[#8a8a8a]">{m.replied}/{m.sent} <span className="text-white font-semibold">{m.rate.toFixed(0)}%</span></span>
                          </div>
                          <div className="h-1.5 bg-[#3a3a3a] rounded-full overflow-hidden">
                            <div className="h-full bg-pp-red" style={{ width: `${Math.min(100, m.rate)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-[#2c2c2c] rounded-lg p-4">
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-xs font-bold text-[#b4b4b4] uppercase tracking-wider">Sender Leaderboard</h3>
                    {avgDaysToReply !== null && <span className="text-[10px] text-[#8a8a8a]">avg reply: {avgDaysToReply}d</span>}
                  </div>
                  {senderRanked.length === 0 ? (
                    <p className="text-xs text-[#8a8a8a] py-2">No senders yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {senderRanked.slice(0, 6).map(s => (
                        <div key={s.sender}>
                          <div className="flex items-baseline justify-between text-xs mb-0.5">
                            <span className="text-[#b4b4b4] truncate">{s.sender}</span>
                            <span className="text-[#8a8a8a]">{s.replied}/{s.sent} <span className="text-white font-semibold">{s.rate.toFixed(0)}%</span></span>
                          </div>
                          <div className="h-1.5 bg-[#3a3a3a] rounded-full overflow-hidden">
                            <div className="h-full bg-[#e93529]" style={{ width: `${Math.min(100, s.rate)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Filters ── */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <input
                type="text"
                placeholder="Search address, contact…"
                value={outreachSearch}
                onChange={e => setOutreachSearch(e.target.value)}
                className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#8a8a8a] w-56 focus:outline-none focus:border-[#e93529]"
              />
              <select value={outreachStatusFilter} onChange={e => setOutreachStatusFilter(e.target.value)} className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#e93529]">
                <option value="All">All Statuses</option>
                <option value="Sent">Sent</option>
                <option value="Responded">Responded</option>
                <option value="Counter">Counter</option>
                <option value="Passed">Passed</option>
                <option value="Pending">Pending</option>
              </select>
              <select value={outreachSentByFilter} onChange={e => setOutreachSentByFilter(e.target.value)} className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#e93529]">
                <option value="All">All Senders</option>
                {senderNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                onClick={() => setOutreachFollowUpOnly(v => !v)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${outreachFollowUpOnly ? 'bg-[#c42d22] text-white' : 'bg-[#3a3a3a] text-[#8a8a8a] hover:text-white'}`}
              >
                Follow Up Needed{followUpNeeded > 0 ? ` (${followUpNeeded})` : ''}
              </button>
              <div className="ml-auto flex items-center gap-1">
                {[
                  { key: 'newest', label: 'Newest' },
                  { key: 'oldest', label: 'Oldest' },
                  { key: 'owner', label: 'Owner A-Z' },
                  { key: 'status', label: 'Status' },
                ].map(s => (
                  <button
                    key={s.key}
                    onClick={() => setOutreachSort(s.key)}
                    className={`text-xs px-2 py-1 rounded ${outreachSort === s.key ? 'bg-[#c42d22] text-white' : 'bg-[#3a3a3a] text-[#8a8a8a] hover:text-white'}`}
                  >
                    {s.label}
                  </button>
                ))}
                <button onClick={exportCSV} className="text-sm bg-[#4a4a4a] hover:bg-[#6a6a6a] text-white px-3 py-1.5 rounded-lg transition-colors ml-2">
                  Export CSV
                </button>
              </div>
            </div>

            {/* ── Outreach entries ── */}
            {filtered.length === 0 ? (
              <div className="text-center py-24 text-[#8a8a8a]">
                <p className="text-5xl mb-4">📬</p>
                <p className="text-lg">{outreachLog.length === 0 ? 'No LOIs sent yet.' : 'No entries match your filters.'}</p>
                {outreachLog.length === 0 && <p className="text-sm mt-2">Generate one from the Properties tab.</p>}
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map(entry => {
                  const prop = properties.find(p => p.id === entry.property_id);
                  const contact = contacts.find(c => c.id === entry.contact_id);
                  const sentDate = entry.sent_at ? new Date(entry.sent_at) : null;
                  const days = daysSinceSent(entry);
                  const isSent = entry.status === 'Sent';
                  const needsFollowUp = isSent && days >= cooldownDays;
                  const isWarning = isSent && days >= 7 && days < cooldownDays;
                  const borderClass = needsFollowUp ? 'border-l-4 border-l-red-500' : isWarning ? 'border-l-4 border-l-yellow-500' : '';

                  return (
                    <div key={entry.id} className={`bg-[#2c2c2c] rounded-lg p-4 hover:bg-[rgba(58,58,58,0.5)] transition-colors ${borderClass}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-semibold truncate">{prop?.address || 'Unknown Property'}</span>
                            {statusBadge(entry.status)}
                            {needsFollowUp && <span className="bg-[rgba(233,53,41,0.2)] text-[#f04f44] text-xs font-medium px-2 py-0.5 rounded-full">Follow up needed · {days}d</span>}
                            {isWarning && <span className="bg-[rgba(200,146,47,0.2)] text-[#c8922f] text-xs font-medium px-2 py-0.5 rounded-full">{days}d, no response</span>}
                            {isSent && days < 7 && <span className="text-[#8a8a8a] text-xs">Sent {days}d ago</span>}
                          </div>
                          <p className="text-[#8a8a8a] text-xs">{[prop?.city, prop?.state].filter(Boolean).join(', ')}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-[#b4b4b4] flex-wrap">
                            <span>Sent to: <span className="text-[#f04f44]">{contact?.email1 || '—'}</span></span>
                            <span>By: {entry.sent_by}</span>
                            {sentDate && <span>{sentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                          </div>
                          <div className="mt-1 text-sm text-[#b4b4b4]">
                            ${Number(entry.loi_psf || 0).toLocaleString()}/SF → ${Number(entry.loi_total || 0).toLocaleString()} total
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <select
                            value={entry.status || 'Sent'}
                            onChange={e => handleUpdateOutreachStatus(entry, e.target.value)}
                            className="bg-[#3a3a3a] border border-[#4a4a4a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#e93529]"
                          >
                            <option value="Sent">Sent</option>
                            <option value="Responded">Responded</option>
                            <option value="Counter">Counter</option>
                            <option value="Passed">Passed</option>
                            <option value="Pending">Pending</option>
                          </select>
                          <button
                            onClick={() => openConversation(entry)}
                            className="text-[#b4b4b4] hover:text-[#b4b4b4] text-xs underline"
                            title="View email thread with this contact"
                          >
                            View Conversation
                          </button>
                          {entry.deal_id && onNavigateToDeal && (
                            <button
                              onClick={() => {
                                const linkedDeal = deals.find(d => d.id === entry.deal_id);
                                if (linkedDeal) onNavigateToDeal(linkedDeal);
                              }}
                              className="text-[#f04f44] hover:text-[#f04f44] text-xs underline"
                            >
                              View Deal →
                            </button>
                          )}
                        </div>
                      </div>
                      {(entry.status === 'Responded' || entry.status === 'Counter' || entry.status === 'Passed') && (
                        <div className="mt-3 pt-3 border-t border-[#3a3a3a]">
                          <label className="text-xs text-[#8a8a8a] mb-1 block">
                            {entry.status === 'Counter' ? 'Counter Terms / Notes' : entry.status === 'Passed' ? 'Why they passed' : 'Response Notes'}
                          </label>
                          <textarea
                            defaultValue={entry.response || ''}
                            onBlur={e => handleUpdateOutreachResponse(entry, e.target.value)}
                            placeholder={entry.status === 'Counter' ? 'e.g. countered at $X/SF, wants 60-day DD…' : 'Add notes about the response…'}
                            className="w-full bg-[#3a3a3a] border border-[#4a4a4a] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#8a8a8a] focus:outline-none focus:ring-2 ring-[#e93529] resize-y min-h-[60px]"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      })()}

      {/* ── Conversation Thread Modal ── */}
      {conversationModal && (() => {
        const { entry, thread, loading: threadLoading, error: threadError } = conversationModal;
        const contact = contacts.find(c => c.id === entry.contact_id);
        const prop = properties.find(p => p.id === entry.property_id);
        return (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(36,36,36,0.4)] backdrop-blur-sm overflow-y-auto p-4 sm:p-8" onClick={() => setConversationModal(null)}>
            <div className="rounded-2xl shadow-2xl w-full max-w-4xl my-4 overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #d8d8d8' }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #d8d8d8' }}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: '#e93529' }} />
                  <div className="min-w-0">
                    <h2 className="text-base font-bold font-montserrat truncate" style={{ color: '#333' }}>
                      Conversation with {contact?.name || contact?.email1 || 'Unknown'}
                    </h2>
                    <p className="text-xs mt-0.5" style={{ color: '#888' }}>
                      {prop?.address || 'Unknown Property'} &middot; ${Number(entry.loi_psf || 0).toLocaleString()}/SF
                    </p>
                  </div>
                </div>
                <button onClick={() => setConversationModal(null)} className="rounded-lg w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#ebebeb] flex-shrink-0" style={{ color: '#666' }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Thread body */}
              <div style={{ backgroundColor: '#f4f4f4', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', padding: '20px 24px' }}>
                {threadLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                ) : threadError ? (
                  <div className="text-center py-8">
                    <p className="text-sm" style={{ color: '#888' }}>
                      Could not load conversation: {threadError}
                    </p>
                    <p className="text-xs mt-2" style={{ color: '#aaa' }}>
                      This may be a permissions issue — ask IT to grant Mail.Read.Shared on the acquisitions@ mailbox.
                    </p>
                  </div>
                ) : thread.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm" style={{ color: '#888' }}>
                      No email conversation found with {contact?.email1 || 'this contact'}.
                    </p>
                    <p className="text-xs mt-2" style={{ color: '#aaa' }}>
                      The LOI may have been sent before Proxy started using acquisitions@, or the recipient hasn't replied yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {thread.map((tm, i) => {
                      const isFromUs = (tm.from?.emailAddress?.address || '').toLowerCase() === 'acquisitions@proximityparks.com';
                      return (
                        <div key={tm.id || i} className="rounded-xl p-4" style={{
                          backgroundColor: isFromUs ? '#fdeceb' : '#ffffff',
                          border: `1px solid ${isFromUs ? '#f5b8b4' : '#e4e4e4'}`,
                        }}>
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              {isFromUs ? (
                                <div className="w-6 h-6 rounded flex items-center justify-center bg-pp-red flex-shrink-0" style={{ padding: 3 }}>
                                  <img src={iconWhite} alt="PP" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
                                </div>
                              ) : (
                                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold" style={{ backgroundColor: '#8a8a8a', color: '#fff' }}>
                                  {(tm.from?.emailAddress?.name || '?')[0].toUpperCase()}
                                </div>
                              )}
                              <span className="text-sm font-semibold" style={{ color: '#333' }}>
                                {isFromUs ? 'ProximityParks Acquisitions' : (tm.from?.emailAddress?.name || tm.from?.emailAddress?.address)}
                              </span>
                            </div>
                            <span className="text-xs flex-shrink-0" style={{ color: '#999' }}>
                              {tm.receivedDateTime ? new Date(tm.receivedDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <div className="text-sm leading-relaxed" style={{ color: '#444' }} dangerouslySetInnerHTML={{ __html: tm.body?.content || tm.bodyPreview || '' }} />
                        </div>
                      );
                    })}
                    <div ref={conversationEndRef} />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

    </div>
        </div>

      {/* ── "Ask Proxy" floating button ── */}
      {!dbChatOpen && (
        <button
          onClick={() => setDbChatOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-pp-red hover:bg-pp-red-light text-white rounded-full p-3 shadow-lg transition-colors font-montserrat"
          title="Ask Proxy"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
        </button>
      )}

      {/* ── Chat Panel ── */}
      <div className={`fixed top-[49px] right-0 bottom-0 w-[400px] bg-[#2c2c2c] border-l border-[#3a3a3a] z-40 flex flex-col transition-transform duration-300 ${dbChatOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#3a3a3a] flex-shrink-0">
          <h3 className="text-sm font-bold text-white font-montserrat">Proxy Sourcing Assistant</h3>
          <button onClick={() => setDbChatOpen(false)} className="text-[#8a8a8a] hover:text-white transition-colors text-lg leading-none">×</button>
        </div>

        {/* Quick chips */}
        <div className="px-3 py-2 border-b border-[#3a3a3a] flex flex-wrap gap-1.5 flex-shrink-0">
          {DB_CHAT_CHIPS.map(c => (
            <button key={c} onClick={() => sendDbChat(c)} className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#b4b4b4] text-xs rounded-full px-3 py-1 transition-colors font-montserrat">{c}</button>
          ))}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ minHeight: 0 }}>
          {dbChatMessages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role !== 'user' && (
                <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mr-2 mt-0.5 bg-pp-red" style={{ padding: 3 }}>
                  <img src={iconWhite} alt="PP" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} draggable={false} />
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${msg.role === 'user' ? 'bg-pp-red rounded-br-sm' : 'rounded-bl-sm'}`}
                style={msg.role === 'user' ? { color: '#ffffff' } : { backgroundColor: '#f4f4f4', color: '#4a4a4a' }}
              >
                {msg.content}
              </div>
            </div>
          ))}
          {dbChatLoading && (
            <div className="flex justify-start">
              <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mr-2 mt-0.5 bg-pp-red" style={{ padding: 3 }}>
                <img src={iconWhite} alt="PP" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} draggable={false} />
              </div>
              <div className="rounded-lg px-3 py-2 flex gap-1" style={{ backgroundColor: '#f4f4f4' }}>
                <span className="w-1.5 h-1.5 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
          <div ref={dbChatBottomRef} />
        </div>

        {/* Input */}
        <div className="px-3 py-3 border-t border-[#3a3a3a] flex-shrink-0">
          <div className="flex gap-2">
            <input
              value={dbChatInput}
              onChange={e => setDbChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendDbChat(); } }}
              placeholder="Ask about properties, outreach…"
              className="flex-1 bg-[#3a3a3a] border border-[#4a4a4a] text-white text-sm rounded-lg px-3 py-2 placeholder:text-[#8a8a8a] focus:outline-none focus:border-pp-red font-montserrat"
            />
            <button onClick={() => sendDbChat()} disabled={dbChatLoading || !dbChatInput.trim()} className="bg-pp-red hover:bg-pp-red-light disabled:opacity-40 text-white px-3 py-2 rounded-lg transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MapView ──────────────────────────────────────────────────────────────────

// Nominatim geocoder with 1.1s rate limit — results cached in Supabase


export default DatabaseView;
